<template>
  <div class="dashboard-layout">
    <!-- WhatsApp Setup Modal -->
    <WhatsAppSetupModal
      :show="showWhatsAppSetupModal"
      @start-setup="startWhatsAppSetup"
      @dismiss="dismissWhatsAppSetupModal"
    />

    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Welcome Section -->
        <div class="welcome-section">
          <div class="welcome-content">
            <h1 class="welcome-title">{{ welcomeMessage }} 👋</h1>
            <p class="welcome-subtitle">Here's what's happening with {{ companyName }} today.</p>
          </div>
          <!-- Enhanced Mode Switcher -->
          <div class="mode-switcher-enhanced">
            <div class="mode-status">
              <div class="mode-icon">{{ isCustomFlowMode ? '⚡' : (isRestaurantMode ? '🍽️' : (isAppointmentMode ? '📅' : '💬')) }}</div>
              <div class="mode-info">
                <span class="mode-description">Current Mode</span>
                <span class="current-mode">{{ currentModeName }}</span>
                <!-- Department Mode Inline (Only in Customer Service mode) -->
                <div v-if="!isAppointmentMode && !isRestaurantMode && !isCustomFlowMode" class="department-mode-inline">
                  <span class="department-label-inline">Multi-Department Routing</span>
                  <div class="department-controls-inline">
                    <label class="switch-compact-small">
                      <input
                        type="checkbox"
                        v-model="isDepartmentMode"
                        @change="confirmDepartmentModeToggle"
                        :disabled="departmentModeLoading"
                      >
                      <span class="slider-compact-small"></span>
                    </label>
                    <button
                      v-if="isDepartmentMode"
                      @click="openDepartmentSettingsModal"
                      class="settings-icon-btn-small"
                      title="Department Settings"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="mode-toggle">
              <button
                @click="switchToCustomerService"
                class="toggle-btn"
                :class="{ active: !isAppointmentMode && !isRestaurantMode && !isCustomFlowMode }"
                :disabled="modeLoading || (!isAppointmentMode && !isRestaurantMode && !isCustomFlowMode)"
              >
                Customer Service
              </button>
              <button
                @click="switchToAppointmentMode"
                class="toggle-btn"
                :class="{ active: isAppointmentMode }"
                :disabled="modeLoading || isAppointmentMode"
              >
                Appointments
              </button>
              <button
                v-if="authStore.user?.role_id === 1"
                @click="switchToRestaurantMode"
                class="toggle-btn"
                :class="{ active: isRestaurantMode }"
                :disabled="modeLoading || isRestaurantMode"
              >
                Restaurant Mode
              </button>
              <button
                v-if="authStore.user?.role_id === 1"
                @click="switchToCustomFlowMode"
                class="toggle-btn toggle-btn-flow"
                :class="{ active: isCustomFlowMode }"
                :disabled="modeLoading || isCustomFlowMode"
              >
                <span v-if="isCustomFlowMode" class="live-dot"></span>
                Custom Flow
              </button>
            </div>
          </div>
        </div>

        <!-- WhatsApp Connection Status -->
        <div
          v-if="hasWhatsAppCredentials !== null"
          class="whatsapp-quick-status"
          :class="{
            'connected': hasWhatsAppCredentials && whatsappStatus.status === 'Connected',
            'disconnected': !hasWhatsAppCredentials || whatsappStatus.status !== 'Connected'
          }"
        >
          <div class="quick-status-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.487"/>
            </svg>
            <div
              v-if="hasWhatsAppCredentials && whatsappStatus.status === 'Connected'"
              class="status-pulse"
            ></div>
          </div>
          <div class="quick-status-content">
            <div class="quick-status-title">
              WhatsApp Connection
              <span v-if="hasWhatsAppCredentials && accountDetails.phone_verified_name" class="verified-name-inline">
                • {{ accountDetails.phone_verified_name }}
              </span>
            </div>
            <div class="quick-status-info">
              <span class="status-indicator" :class="{
                'connected': hasWhatsAppCredentials && whatsappStatus.status === 'Connected',
                'disconnected': !hasWhatsAppCredentials || whatsappStatus.status !== 'Connected'
              }">
                {{ hasWhatsAppCredentials ? (whatsappStatus.status || 'Unknown') : 'Not Configured' }}
              </span>
            </div>

            <!-- Account Details Section (only when connected) -->
            <div v-if="hasWhatsAppCredentials" class="account-details-inline">
              <div class="account-status-row">
                <div class="status-item-inline">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M9 11l3 3L22 4"/>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                  </svg>
                  <span :class="['status-badge-small', getReviewStatusClass(accountDetails.account_review_status)]">
                    {{ formatReviewStatus(accountDetails.account_review_status) }}
                  </span>
                </div>
                <div class="status-item-inline">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23,12L20.56,9.22L20.9,5.54L17.29,4.72L15.4,1.54L12,3L8.6,1.54L6.71,4.72L3.1,5.53L3.44,9.21L1,12L3.44,14.78L3.1,18.47L6.71,19.29L8.6,22.47L12,21L15.4,22.46L17.29,19.28L20.9,18.46L20.56,14.78L23,12M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z"/>
                  </svg>
                  <span :class="['status-badge-small', getVerificationStatusClass(accountDetails.business_verification_status)]">
                    {{ formatVerificationStatus(accountDetails.business_verification_status) }}
                  </span>
                </div>
                <div class="status-item-inline">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span :class="['status-badge-small', 'messaging-limit', `limit-${getMessagingLimitTier(accountDetails.messaging_limit)}`]">
                    {{ formatMessagingLimit(accountDetails.messaging_limit) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mode Switch Confirmation Modal -->
        <div v-if="showConfirmModal" class="modal-overlay" @click="closeConfirmModal">
          <div class="confirmation-modal" @click.stop>
            <div class="modal-header">
              <div class="modal-icon">{{
                pendingMode === 'appointment' ? '📅' :
                (pendingMode === 'restaurant' ? '🍽️' :
                (pendingMode === 'flow' ? '⚡' : '🎧'))
              }}</div>
              <h3 class="modal-title">Switch Service Mode?</h3>
            </div>
            <div class="modal-body">
              <p class="modal-message">
                Are you sure you want to switch to
                <strong>{{
                  pendingMode === 'appointment' ? 'Appointment Mode' :
                  (pendingMode === 'restaurant' ? 'Restaurant Mode' :
                  (pendingMode === 'flow' ? 'Custom Flow Mode' : 'Customer Service Mode'))
                }}</strong>?
              </p>
              <p class="modal-submessage">
                This will change how your service operates and may affect ongoing interactions.
              </p>
            </div>
            <div class="modal-actions">
              <button @click="closeConfirmModal" class="modal-btn cancel-btn">
                Cancel
              </button>
              <button @click="confirmModeSwitch" class="modal-btn confirm-btn" :disabled="modeLoading">
                <span v-if="modeLoading" class="loading-spinner-small"></span>
                {{ modeLoading ? 'Switching...' : 'Yes, Switch Mode' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Products/Services Warning Banner -->
        <div v-if="shouldShowProductsServicesWarning" class="products-services-warning">
          <div class="warning-icon">⚠️</div>
          <div class="warning-content">
            <h3 class="warning-title">Products/Services Required</h3>
            <p class="warning-message">
              To enable WhatsApp AI chatbot in Customer Service mode, you need to add your products and services first.
            </p>
          </div>
          <a href="/products-services" class="warning-action-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px;">
              <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
            </svg>
            Add Products/Services
          </a>
        </div>

        <!-- AI Analytics Overview -->
        <div v-if="overviewData.success" class="analytics-section">
          <div class="section-header">
            <h2 class="section-title">🤖 AI Performance Insights</h2>
            <div class="insights-filters">
              <select v-model="selectedTimeframe" @change="updateOverviewData" class="filter-select">
                <option value="">Last 30 Days</option>
                <option value="7">Last 7 Days</option>
                <option value="14">Last 14 Days</option>
                <option value="30">Last 30 Days</option>
              </select>
              <select v-model="selectedAnalysisType" @change="updateOverviewData" class="filter-select">
                <option value="">All Types</option>
                <option value="call">Phone Calls</option>
                <option value="whatsapp_conversation">WhatsApp</option>
              </select>
            </div>
          </div>

          <!-- Key Metrics Grid -->
          <div class="metrics-grid">
            <div class="metric-card primary">
              <div class="metric-icon">📊</div>
              <div class="metric-content">
                <div class="metric-number">{{ overviewData.overall_statistics?.total_analyses || 0 }}</div>
                <div class="metric-label">Total Analyses</div>
                <div class="metric-period">{{ formatPeriod(overviewData.overview_period) }}</div>
              </div>
            </div>

            <div class="metric-card success">
              <div class="metric-icon">😊</div>
              <div class="metric-content">
                <div class="metric-number">{{ overviewData.overall_statistics?.average_satisfaction_score || 0 }}%</div>
                <div class="metric-label">Avg Satisfaction</div>
                <div class="metric-trend positive">+5.2% improvement</div>
              </div>
            </div>

            <div class="metric-card info">
              <div class="metric-icon">⚡</div>
              <div class="metric-content">
                <div class="metric-number">{{ overviewData.overall_statistics?.average_performance_score || 0 }}</div>
                <div class="metric-label">Performance Score</div>
                <div class="metric-trend positive">Above target</div>
              </div>
            </div>

            <div class="metric-card warning">
              <div class="metric-icon">⚠️</div>
              <div class="metric-content">
                <div class="metric-number">{{ overviewData.risk_analysis?.high_risk_conversations || 0 }}</div>
                <div class="metric-label">High Risk Cases</div>
                <div class="metric-trend neutral">{{ overviewData.overall_statistics?.average_escalation_risk || 0 }}% avg risk</div>
              </div>
            </div>
          </div>

          <!-- Charts and Insights -->
          <div class="insights-grid">
            <!-- Satisfaction Distribution -->
            <div class="insight-card">
              <div class="insight-header">
                <h3 class="insight-title">Customer Satisfaction</h3>
                <div class="insight-badge success">{{ getSatisfactionRate() }}% satisfied</div>
              </div>
              <div class="satisfaction-chart">
                <div v-for="item in overviewData.satisfaction_distribution" :key="item.customer_satisfaction_prediction"
                     class="satisfaction-item">
                  <div class="satisfaction-label">{{ formatSatisfactionLabel(item.customer_satisfaction_prediction) }}</div>
                  <div class="satisfaction-bar">
                    <div class="satisfaction-fill"
                         :class="item.customer_satisfaction_prediction"
                         :style="{ width: item.percentage + '%' }"></div>
                  </div>
                  <div class="satisfaction-value">{{ item.count }} ({{ Math.round(item.percentage) }}%)</div>
                </div>
              </div>
            </div>

            <!-- Top Performing Agents -->
            <div class="insight-card">
              <div class="insight-header">
                <h3 class="insight-title">🏆 Top Performing Agents</h3>
                <router-link to="/ai-analysis" class="insight-link">View Details</router-link>
              </div>
              <div class="agents-ranking">
                <div v-for="(agent, index) in overviewData.top_performing_agents?.slice(0, 4)"
                     :key="agent.agent_id" class="agent-rank-item">
                  <div class="rank-number">{{ index + 1 }}</div>
                  <div class="agent-info">
                    <div class="agent-name">{{ agent.agent_name }}</div>
                    <div class="agent-stats">
                      {{ agent.total_conversations }} calls • {{ Math.round(agent.avg_performance) }}% performance
                    </div>
                  </div>
                  <div class="satisfaction-score" :class="getSatisfactionClass(agent.satisfaction_rate)">
                    {{ Math.round(agent.satisfaction_rate) }}%
                  </div>
                </div>
              </div>
            </div>

            <!-- Common Issues -->
            <div class="insight-card">
              <div class="insight-header">
                <h3 class="insight-title">⚠️ Common Issues</h3>
                <span class="insight-count">Top {{ overviewData.most_common_issues?.length || 0 }}</span>
              </div>
              <div class="issues-list">
                <div v-for="issue in overviewData.most_common_issues?.slice(0, 5)"
                     :key="issue.issue" class="issue-item">
                  <div class="issue-text">{{ issue.issue }}</div>
                  <div class="issue-count">{{ issue.count }}</div>
                </div>
              </div>
            </div>

            <!-- Critical Analyses -->
            <div class="insight-card critical">
              <div class="insight-header">
                <h3 class="insight-title">🚨 Critical Analyses</h3>
                <span class="insight-count">{{ overviewData.critical_analyses?.length || 0 }} urgent</span>
              </div>
              <div class="critical-list">
                <div v-for="analysis in overviewData.critical_analyses?.slice(0, 3)"
                     :key="analysis.id" class="critical-item">
                  <div class="critical-info">
                    <div class="critical-phone">{{ analysis.customer_phone }}</div>
                    <div class="critical-summary">{{ analysis.ai_summary.substring(0, 60) }}...</div>
                  </div>
                  <div class="critical-risk">{{ Math.round(analysis.escalation_risk_score) }}%</div>
                </div>
              </div>
              <router-link to="/ai-analysis" class="view-all-link">View All Critical Cases →</router-link>
            </div>
          </div>
        </div>

        <!-- 📊 Business Insights Section -->
        <div class="insights-section">
          <div class="insights-header">
            <h2 class="insights-title">📊 Business Insights</h2>
            <p class="insights-subtitle">Monitor your lead generation and communication channels</p>
          </div>

          <div class="insights-grid business-insights-grid">
            <!-- Lead Intelligence Card -->
            <div class="insight-card lead-insights">
              <div class="insight-header">
                <div class="insight-title-with-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" class="lead-intelligence-icon">
                    <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
                  </svg>
                  <div class="insight-title-content">
                    <h3 class="insight-title">Lead Intelligence Center</h3>
                    <p class="insight-subtitle">Track and analyze your lead generation performance</p>
                  </div>
                </div>
                <span class="insight-count" v-if="!leadData.loading">{{ leadData.todayStats?.count || 0 }} today</span>
              </div>

              <!-- Show loading state -->
              <div v-if="leadData.loading" class="insights-loading">
                <div class="loading-spinner"></div>
                <p>Loading...</p>
              </div>

              <!-- Show error state -->
              <div v-else-if="leadData.error" class="insights-error">
                <div class="error-icon">⚠️</div>
                <p>{{ leadData.error }}</p>
              </div>

              <!-- Show lead stats -->
              <div v-else class="lead-stats-grid">
                <div class="stat-card primary">
                  <div class="stat-number">{{ leadData.todayStats?.count || 0 }}</div>
                  <div class="stat-label">Today</div>
                  <div class="stat-rate">{{ leadData.todayStats?.completion_rate?.toFixed(1) || 0 }}%</div>
                </div>
                <div class="stat-card">
                  <div class="stat-number">{{ leadData.weekStats?.count || 0 }}</div>
                  <div class="stat-label">Week</div>
                  <div class="stat-rate">{{ leadData.weekStats?.completion_rate?.toFixed(1) || 0 }}%</div>
                </div>
                <div class="stat-card">
                  <div class="stat-number">{{ leadData.monthStats?.count || 0 }}</div>
                  <div class="stat-label">Month</div>
                  <div class="stat-rate">{{ leadData.monthStats?.completion_rate?.toFixed(1) || 0 }}%</div>
                </div>
              </div>

              <!-- Recent activity -->
              <div class="recent-activity" v-if="leadData.recentCompletions?.length">
                <h4 class="activity-title">Recent Completions</h4>
                <div class="activity-list">
                  <div class="activity-item" v-for="completion in leadData.recentCompletions.slice(0, 2)" :key="completion.phone_number">
                    <div class="activity-dot success"></div>
                    <div class="activity-info">
                      <div class="activity-text">{{ completion.name }} - {{ completion.company_name }}</div>
                      <div class="activity-time">{{ formatTime(completion.completed_at) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Motivational Message -->
              <div class="motivational-message" v-if="leadData.motivationalMessage">
                <div class="motivational-content">
                  <span class="motivational-text">{{ leadData.motivationalMessage }}</span>
                </div>
              </div>
            </div>

            <!-- WhatsApp Connect Card -->
            <div class="insight-card whatsapp-insights">
              <div class="insight-header">
                <div class="insight-title-with-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" class="whatsapp-icon">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.487"/>
                  </svg>
                  <div class="insight-title-content">
                    <h3 class="insight-title">WhatsApp Connect Insights</h3>
                    <p class="insight-subtitle">Monitor your WhatsApp conversation activity</p>
                  </div>
                </div>
                <span class="insight-count" v-if="!whatsappInsights.loading && hasWhatsAppCredentials">{{ whatsappInsights.todays_conversations || 0 }} today</span>
              </div>

              <!-- Show WhatsApp not connected message -->
              <div v-if="!hasWhatsAppCredentials" class="qualibot-disabled">
                <div class="disabled-icon">🔌</div>
                <p>WhatsApp is not connected</p>
                <small>Connect WhatsApp to see insights</small>
              </div>

              <!-- Show loading state -->
              <div v-else-if="whatsappInsights.loading" class="insights-loading">
                <div class="loading-spinner"></div>
                <p>Loading...</p>
              </div>

              <!-- Show error state -->
              <div v-else-if="whatsappInsights.error" class="insights-error">
                <div class="error-icon">⚠️</div>
                <p>{{ whatsappInsights.error }}</p>
              </div>

              <!-- Show WhatsApp stats -->
              <div v-else class="whatsapp-stats-grid">
                <div class="stat-card primary">
                  <div class="stat-number">{{ whatsappInsights.todays_conversations || 0 }}</div>
                  <div class="stat-label">Today</div>
                </div>
                <div class="stat-card">
                  <div class="stat-number">{{ whatsappInsights.yesterdays_conversations || 0 }}</div>
                  <div class="stat-label">Yesterday</div>
                </div>
                <div class="stat-card">
                  <div class="stat-number">{{ whatsappInsights.week_conversations || 0 }}</div>
                  <div class="stat-label">Week</div>
                </div>
                <div class="stat-card">
                  <div class="stat-number">{{ whatsappInsights.month_conversations || 0 }}</div>
                  <div class="stat-label">Month</div>
                </div>
              </div>

              <!-- Change indicator -->
              <div class="change-summary" v-if="whatsappInsights.change_from_yesterday">
                <div class="change-indicator" :class="whatsappInsights.change_from_yesterday?.absolute >= 0 ? 'positive' : 'negative'">
                  <span class="change-icon">{{ whatsappInsights.change_from_yesterday?.absolute >= 0 ? '📈' : '📉' }}</span>
                  <span class="change-text">
                    {{ whatsappInsights.change_from_yesterday?.absolute >= 0 ? '+' : '' }}{{ whatsappInsights.change_from_yesterday?.absolute || 0 }} vs yesterday
                  </span>
                  <span class="change-percent">{{ whatsappInsights.change_from_yesterday?.percentage?.toFixed(1) || 0 }}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dashboard Grid -->
        <div class="dashboard-grid">

          <!-- WhatsApp Connect Status Card -->
          <div class="dashboard-card whatsapp-hero-card" :class="{ 'setup-needed': hasWhatsAppCredentials === false }">
            <!-- No Credentials State -->
            <div v-if="hasWhatsAppCredentials === false" class="whatsapp-setup-state">
              <div class="setup-visual">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  alt="WhatsApp"
                  class="whatsapp-hero-icon"
                />
                <div class="setup-badge">Not Configured</div>
              </div>
              <div class="setup-content">
                <h3 class="setup-title">Connect WhatsApp to Get Started</h3>
                <p class="setup-description">Set up your WhatsApp Business integration to unlock powerful messaging features.</p>
                <button @click="startWhatsAppSetup" class="setup-tutorial-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
                  </svg>
                  <span>Start Setup Tutorial</span>
                </button>
              </div>
            </div>

            <!-- Normal State (has credentials) -->
            <div v-else class="whatsapp-hero-content">
              <div class="whatsapp-visual-section">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                  alt="WhatsApp"
                  class="whatsapp-hero-icon"
                />
                <div v-if="whatsappStatus.status === 'Connected'" class="whatsapp-glow active"></div>
              </div>
              <div class="whatsapp-info-section">
                <h2 class="whatsapp-hero-title">
                  <span class="platform-label">WHATSAPP</span>
                  <span class="connect-label">CONNECTION</span>
                </h2>
                <div class="whatsapp-status-row">
                  <span class="status-label">Status:</span>
                  <span class="status-value" :class="{ connected: whatsappStatus.status === 'Connected' }">
                    {{ whatsappStatus.status || 'Unknown' }}
                  </span>
                </div>
                <div class="connection-details">
                  <div v-if="whatsappStatus.uniqueSessionId" class="session-info">
                    <small class="session-id">ID: {{ whatsappStatus.uniqueSessionId.substring(0, 8) }}...</small>
                  </div>
                  <div v-if="whatsappStatus.setupMethod" class="setup-method-badge" :class="{ 'embedded': whatsappStatus.setupMethod === 'embedded_signup' }">
                    <svg v-if="whatsappStatus.setupMethod === 'embedded_signup'" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"/>
                    </svg>
                    <span>{{ whatsappStatus.setupMethod === 'embedded_signup' ? 'Meta OAuth' : 'Manual Config' }}</span>
                  </div>
                </div>
              </div>
              <div class="whatsapp-actions">
                <!-- Initialize Button (only show when not connected) -->
                <button
                  v-if="whatsappStatus.status !== 'Connected'"
                  @click="initializeWhatsApp"
                  :disabled="whatsappInitializeLoading"
                  class="whatsapp-hero-initialize"
                >
                  <span class="initialize-icon">🚀</span>
                  <span v-if="whatsappInitializeLoading" class="initialize-text">INITIALIZING...</span>
                  <span v-else class="initialize-text">INITIALIZE</span>
                </button>
                <!-- Recheck Button -->
                <button
                  @click="recheckWhatsAppStatus"
                  :disabled="whatsappStatusLoading"
                  class="whatsapp-hero-recheck"
                >
                  <span class="recheck-icon">🔄</span>
                  <span v-if="whatsappStatusLoading" class="recheck-text">CHECKING...</span>
                  <span v-else class="recheck-text">RECHECK</span>
                </button>
              </div>
            </div>
          </div>

          <!-- AI QUALIBOT Status Card -->
          <div class="dashboard-card qualibot-hero-card" :class="{ 'integration-disabled': hasWhatsAppCredentials === false }">
            <!-- Disabled Overlay -->
            <div v-if="hasWhatsAppCredentials === false" class="integration-disabled-overlay">
              <div class="disabled-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="disabled-icon">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.1 14.8,9.5V11C15.4,11 16,11.6 16,12.3V15.8C16,16.4 15.4,17 14.7,17H9.2C8.6,17 8,16.4 8,15.7V12.2C8,11.6 8.6,11 9.2,11V9.5C9.2,8.1 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,9.5V11H13.5V9.5C13.5,8.7 12.8,8.2 12,8.2Z"/>
                </svg>
                <h3 class="disabled-title">WhatsApp Required</h3>
                <p class="disabled-message">Connect WhatsApp first to enable QualiBot AI</p>
              </div>
            </div>

            <div class="qualibot-hero-content">
              <div class="qualibot-visual-section">
                <img
                  :src="dashboardData.qualiBotStatus?.qualibotEnabled
                    ? 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/qualidevian-ca1k33/assets/6k33j5h1fmtv/ezgif-7-3cce5d3f00.gif'
                    : 'https://storage.googleapis.com/flutterflow-io-6f20.appspot.com/projects/qualidevian-ca1k33/assets/z9ef9j52nd27/ezgif-3-b93fcc9603.gif'"
                  alt="QUALIBOT Animation"
                  class="qualibot-hero-gif"
                />
                <div v-if="dashboardData.qualiBotStatus?.qualibotEnabled" class="qualibot-glow active"></div>
              </div>

              <div class="qualibot-info-section">
                <h2 class="qualibot-hero-title">
                  <span class="ai-label">AI</span>
                  <span class="qualibot-name">QUALI-BOT</span>
                </h2>
                <div class="qualibot-status-row">
                  <span class="status-label">Status:</span>
                  <span class="status-value" :class="{ connected: dashboardData.qualiBotStatus?.qualibotEnabled }">
                    {{ dashboardData.qualiBotStatus?.qualibotEnabled ? 'CONNECTED' : 'DISCONNECTED' }}
                  </span>
                </div>
              </div>

              <button
                @click="toggleQualiBot"
                :disabled="qualiBotLoading || hasWhatsAppCredentials === false || dashboardData.qualiBotStatus?.block_chatbot === 1"
                class="qualibot-hero-toggle"
                :class="{ active: dashboardData.qualiBotStatus?.qualibotEnabled }"
              >
                <span class="toggle-icon">⚡</span>
                <span v-if="qualiBotLoading" class="toggle-text">Processing...</span>
                <span v-else-if="dashboardData.qualiBotStatus?.block_chatbot === 1" class="toggle-text">BLOCKED</span>
                <span v-else class="toggle-text">
                  {{ dashboardData.qualiBotStatus?.qualibotEnabled ? 'DEACTIVATE' : 'ACTIVATE' }}
                </span>
              </button>
            </div>
          </div>

          <!-- Zoho CRM Integration Status Card -->
          <div class="dashboard-card crm-hero-card" :class="{ 'integration-disabled': hasWhatsAppCredentials === false }">
            <!-- Disabled Overlay -->
            <div v-if="hasWhatsAppCredentials === false" class="integration-disabled-overlay">
              <div class="disabled-content">
                <div class="disabled-logos-row">
                  <img src="https://www.zohowebstatic.com/sites/zweb/images/commonroot/zoho-logo-web.svg" alt="Zoho" class="disabled-logo" />
                </div>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="disabled-icon">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.1 14.8,9.5V11C15.4,11 16,11.6 16,12.3V15.8C16,16.4 15.4,17 14.7,17H9.2C8.6,17 8,16.4 8,15.7V12.2C8,11.6 8.6,11 9.2,11V9.5C9.2,8.1 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,9.5V11H13.5V9.5C13.5,8.7 12.8,8.2 12,8.2Z"/>
                </svg>
                <h3 class="disabled-title">WhatsApp Required</h3>
                <p class="disabled-message">Connect WhatsApp first to enable CRM integration</p>
              </div>
            </div>

            <div class="crm-hero-content">
              <div class="crm-visual-section">
                <img
                  src="https://www.zohowebstatic.com/sites/zweb/images/commonroot/zoho-logo-web.svg"
                  alt="Zoho CRM"
                  class="crm-hero-icon"
                />
              </div>
              <div class="crm-info-section">
                <h2 class="crm-hero-title">
                  <span class="platform-label">ZOHO CRM</span>
                  <span class="connect-label">INTEGRATION</span>
                </h2>
                <div class="crm-status-row">
                  <span class="status-label">Status:</span>
                  <span class="status-value" :class="{
                    connected: crmStatus.connected && !crmStatus.token_expired,
                    warning: crmStatus.token_expired
                  }">
                    {{ crmStatusText }}
                  </span>
                </div>
                <div v-if="crmStatus.connected && crmStatus.last_sync_at" class="sync-info">
                  <small class="sync-time">Last sync: {{ formatLastSync(crmStatus.last_sync_at) }}</small>
                </div>
              </div>
              <div class="crm-actions">
                <button
                  v-if="!hasCRMAccess"
                  disabled
                  class="crm-hero-restricted"
                  title="Available only for Starter & Pro Plans"
                >
                  <span class="restricted-icon">🔒</span>
                  <span class="restricted-text">Available only for Starter & Pro Plans</span>
                </button>
                <button
                  v-else-if="!crmStatus.connected"
                  @click="hasWhatsAppCredentials !== false && $router.push('/zoho-integration')"
                  :disabled="hasWhatsAppCredentials === false"
                  class="crm-hero-connect"
                >
                  <span class="connect-icon">🔗</span>
                  <span class="connect-text">CONNECT</span>
                </button>
                <button
                  v-else
                  @click="hasWhatsAppCredentials !== false && $router.push('/zoho-integration')"
                  :disabled="hasWhatsAppCredentials === false"
                  class="crm-hero-manage"
                >
                  <span class="manage-icon">⚙️</span>
                  <span class="manage-text">MANAGE</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Odoo CRM Integration Status Card -->
          <div class="dashboard-card crm-hero-card odoo-hero-card" :class="{ 'integration-disabled': hasWhatsAppCredentials === false }">
            <!-- Disabled Overlay -->
            <div v-if="hasWhatsAppCredentials === false" class="integration-disabled-overlay">
              <div class="disabled-content">
                <div class="disabled-logos-row">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/4/4d/Odoo_logo_rgb.svg" alt="Odoo" class="disabled-logo disabled-logo-odoo" />
                </div>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="disabled-icon">
                  <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.1 14.8,9.5V11C15.4,11 16,11.6 16,12.3V15.8C16,16.4 15.4,17 14.7,17H9.2C8.6,17 8,16.4 8,15.7V12.2C8,11.6 8.6,11 9.2,11V9.5C9.2,8.1 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,9.5V11H13.5V9.5C13.5,8.7 12.8,8.2 12,8.2Z"/>
                </svg>
                <h3 class="disabled-title">WhatsApp Required</h3>
                <p class="disabled-message">Connect WhatsApp first to enable Odoo integration</p>
              </div>
            </div>

            <div class="crm-hero-content">
              <div class="crm-visual-section">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/4/4d/Odoo_logo_rgb.svg"
                  alt="Odoo CRM"
                  class="crm-hero-icon"
                />
              </div>
              <div class="crm-info-section">
                <h2 class="crm-hero-title">
                  <span class="platform-label">ODOO CRM</span>
                  <span class="connect-label">INTEGRATION</span>
                </h2>
                <div class="crm-status-row">
                  <span class="status-label">Status:</span>
                  <span class="status-value" :class="{
                    connected: odooStatus.connected && odooStatus.connection_valid,
                    warning: odooStatus.connected && !odooStatus.connection_valid
                  }">
                    {{ odooStatusText }}
                  </span>
                </div>
                <div v-if="odooStatus.connected && odooStatus.last_sync_at" class="sync-info">
                  <small class="sync-time">Last sync: {{ formatLastSync(odooStatus.last_sync_at) }}</small>
                </div>
              </div>
              <div class="crm-actions">
                <button
                  v-if="!hasCRMAccess"
                  disabled
                  class="crm-hero-restricted"
                  title="Available only for Starter & Pro Plans"
                >
                  <span class="restricted-icon">🔒</span>
                  <span class="restricted-text">Available only for Starter & Pro Plans</span>
                </button>
                <button
                  v-else-if="!odooStatus.connected"
                  @click="hasWhatsAppCredentials !== false && $router.push('/odoo-integration')"
                  :disabled="hasWhatsAppCredentials === false"
                  class="crm-hero-connect"
                >
                  <span class="connect-icon">🔗</span>
                  <span class="connect-text">CONNECT</span>
                </button>
                <button
                  v-else
                  @click="hasWhatsAppCredentials !== false && $router.push('/odoo-integration')"
                  :disabled="hasWhatsAppCredentials === false"
                  class="crm-hero-manage"
                >
                  <span class="manage-icon">⚙️</span>
                  <span class="manage-text">MANAGE</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Active Agents Card -->
          <div class="dashboard-card agents-tabbed-card">
            <div class="agents-header">
              <h3 class="agents-title">👥 Team Status</h3>
              <div class="agents-tabs">
                <button
                  class="tab-btn"
                  :class="{ active: activeTab === 'online' }"
                  @click="activeTab = 'online'"
                >
                  Online ({{ dashboardData.activeAgents?.activeAgentsCount || 0 }})
                </button>
                <button
                  class="tab-btn"
                  :class="{ active: activeTab === 'offline' }"
                  @click="activeTab = 'offline'"
                >
                  Offline ({{ dashboardData.activeAgents?.offlineAgentsCount || 0 }})
                </button>
              </div>
            </div>

            <div class="agents-tab-content">
              <!-- Online Tab -->
              <div v-if="activeTab === 'online'" class="tab-panel">
                <div v-if="dashboardData.activeAgents?.activeAgents?.length" class="agents-list">
                  <div v-for="agent in dashboardData.activeAgents.activeAgents" :key="agent.id" class="agent-item online">
                    <div class="agent-status">
                      <div class="status-dot online"></div>
                      <span class="status-text">ONLINE</span>
                    </div>
                    <div class="agent-info">
                      <div class="agent-name">{{ agent.name }}</div>
                      <div class="agent-meta">{{ agent.agent_role_name }} • {{ agent.department_name }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-tab">
                  <div class="empty-icon">🔴</div>
                  <div class="empty-text">No agents online</div>
                </div>
              </div>

              <!-- Offline Tab -->
              <div v-if="activeTab === 'offline'" class="tab-panel">
                <div v-if="dashboardData.activeAgents?.offlineAgents?.length" class="agents-list">
                  <div v-for="agent in dashboardData.activeAgents.offlineAgents" :key="agent.id" class="agent-item offline">
                    <div class="agent-status">
                      <div class="status-dot offline"></div>
                      <span class="status-text">OFFLINE</span>
                    </div>
                    <div class="agent-info">
                      <div class="agent-name">{{ agent.name }}</div>
                      <div class="agent-meta">{{ agent.agent_role_name }} • {{ agent.department_name }}</div>
                    </div>
                  </div>
                </div>
                <div v-else class="empty-tab">
                  <div class="empty-icon">😴</div>
                  <div class="empty-text">No agents offline</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- QualiBot Confirmation Modal -->
        <div v-if="showQualiBotConfirmModal" class="modal-overlay" @click="closeQualiBotConfirmModal">
          <div class="confirmation-modal" @click.stop>
            <div class="modal-header">
              <div class="modal-icon">{{ dashboardData.qualiBotStatus?.qualibotEnabled ? '⚠️' : '🤖' }}</div>
              <h3 class="modal-title">
                {{ dashboardData.qualiBotStatus?.qualibotEnabled ? 'Deactivate' : 'Activate' }} QualiBot
              </h3>
            </div>
            <div class="modal-body">
              <p v-if="dashboardData.qualiBotStatus?.qualibotEnabled" class="modal-message">
                Are you sure you want to deactivate the AI QualiBot? This will stop all automated responses and customer interactions.
              </p>
              <p v-else class="modal-message">
                Are you sure you want to activate the AI QualiBot? This will enable automated responses and customer interactions.
              </p>
            </div>
            <div class="modal-actions">
              <button @click="closeQualiBotConfirmModal" class="modal-btn cancel-btn">
                Cancel
              </button>
              <button @click="confirmQualiBotToggle" class="modal-btn confirm-btn" :disabled="qualiBotLoading">
                <span v-if="qualiBotLoading" class="loading-spinner-small"></span>
                {{ qualiBotLoading ? 'Processing...' : (dashboardData.qualiBotStatus?.qualibotEnabled ? 'Yes, Deactivate' : 'Yes, Activate') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Department Mode Warning Modal -->
        <div v-if="showDepartmentWarningModal" class="modal-overlay" @click="cancelDepartmentModeToggle">
          <div class="confirm-modal" @click.stop>
            <div class="confirm-icon warning">⚠️</div>
            <h3 class="confirm-title">Enable Multi-Department Routing?</h3>
            <p class="confirm-message">
              When department routing is enabled, the <strong>AI chatbot will be completely disabled</strong>.
              All customer messages will be routed to your agents only.
            </p>
            <div class="confirm-actions">
              <button @click="cancelDepartmentModeToggle" class="confirm-cancel-btn">
                Cancel
              </button>
              <button @click="proceedWithDepartmentModeToggle" class="confirm-btn warning">
                Enable
              </button>
            </div>
          </div>
        </div>

        <!-- Department Settings Modal -->
        <div v-if="showDepartmentSettingsModal" class="modal-overlay" @click="closeDepartmentSettingsModal">
          <div class="department-settings-modal-wide" @click.stop>
            <div class="modal-header">
              <h3 class="modal-title">Multi-Department Settings</h3>
              <button @click="closeDepartmentSettingsModal" class="modal-close-btn">×</button>
            </div>
            <div class="modal-body-split">
              <!-- Left Panel: Settings -->
              <div class="settings-panel">
                <!-- Timeout Hours Selector -->
                <div class="modal-setting-row">
                  <label class="modal-setting-label">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
                    </svg>
                    Session Timeout
                  </label>
                  <select v-model="departmentTimeoutHours" class="modal-select">
                    <option :value="24">24 Hours</option>
                    <option :value="48">48 Hours</option>
                    <option :value="72">72 Hours (3 days)</option>
                    <option :value="168">1 Week</option>
                  </select>
                </div>

                <!-- Header Type Selector -->
                <div class="modal-setting-row">
                  <label class="modal-setting-label">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.5,13.5L11,16.5L14.5,12L19,18H5M21,19V5C21,3.89 20.1,3 19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19Z"/>
                    </svg>
                    Header Type
                  </label>
                  <select v-model="departmentHeaderType" @change="onHeaderTypeChange" class="modal-select">
                    <option value="text">Text Only</option>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="document">Document (PDF)</option>
                  </select>
                </div>

                <!-- Media Upload (Only shown when type is not 'text') -->
                <div v-if="departmentHeaderType !== 'text'" class="modal-setting-row">
                  <label class="modal-setting-label">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z"/>
                    </svg>
                    Upload {{ departmentHeaderType }}
                  </label>
                  <div class="file-upload-area">
                    <input
                      type="file"
                      ref="mediaFileInput"
                      @change="handleMediaFileChange"
                      :accept="getAcceptedFileTypes()"
                      class="file-input-hidden"
                      id="media-file-input"
                    >
                    <label for="media-file-input" class="file-upload-btn">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                      </svg>
                      Choose File
                    </label>
                    <span v-if="selectedMediaFile" class="file-name">{{ selectedMediaFile.name }}</span>
                    <span v-else-if="departmentHeaderMediaUrl" class="file-name">{{ getMediaFileName() }}</span>
                    <span v-else class="file-placeholder">No file selected</span>
                    <button
                      v-if="departmentHeaderMediaUrl || selectedMediaFile"
                      @click="deleteMedia"
                      class="delete-media-btn"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                  <!-- File Requirements -->
                  <div class="file-requirements">
                    <div v-if="departmentHeaderType === 'image'" class="requirement-item">
                      <strong>Image Requirements:</strong>
                      <ul>
                        <li>Maximum file size: 5 MB</li>
                        <li>Supported formats: JPG, PNG</li>
                      </ul>
                    </div>
                    <div v-if="departmentHeaderType === 'video'" class="requirement-item">
                      <strong>Video Requirements:</strong>
                      <ul>
                        <li>Maximum file size: 16 MB</li>
                        <li>Aspect ratio: 16:9 or 4:3 (landscape only, no vertical videos)</li>
                        <li>Supported formats: MP4, 3GP</li>
                      </ul>
                    </div>
                    <div v-if="departmentHeaderType === 'document'" class="requirement-item">
                      <strong>Document Requirements:</strong>
                      <ul>
                        <li>Maximum file size: 100 MB</li>
                        <li>Supported format: PDF only</li>
                      </ul>
                    </div>
                  </div>
                  <div v-if="uploadError" class="upload-error">{{ uploadError }}</div>
                </div>

                <!-- Text Headers (Only shown when type is 'text') -->
                <template v-if="departmentHeaderType === 'text'">
                  <div class="modal-setting-row">
                    <label class="modal-setting-label">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21,11H6.83L10.41,7.41L9,6L3,12L9,18L10.41,16.59L6.83,13H21V11Z"/>
                      </svg>
                      Arabic Header
                    </label>
                    <input
                      type="text"
                      v-model="departmentHeaderAr"
                      placeholder="اختر القسم"
                      class="modal-input"
                    >
                  </div>

                  <div class="modal-setting-row">
                    <label class="modal-setting-label">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,13H9.83L6.24,16.59L7.66,18L13.66,12L7.66,6L6.24,7.41L9.83,11H3V13Z"/>
                      </svg>
                      English Header
                    </label>
                    <input
                      type="text"
                      v-model="departmentHeaderEn"
                      placeholder="Select Department"
                      class="modal-input"
                    >
                  </div>
                </template>

                <!-- Arabic Body -->
                <div class="modal-setting-row">
                  <label class="modal-setting-label">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z"/>
                    </svg>
                    Arabic Message
                  </label>
                  <textarea
                    v-model="departmentBodyAr"
                    placeholder="مرحباً! من فضلك اختر القسم الذي تريد التواصل معه:"
                    rows="3"
                    class="modal-textarea"
                  ></textarea>
                </div>

                <!-- English Body -->
                <div class="modal-setting-row">
                  <label class="modal-setting-label">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z"/>
                    </svg>
                    English Message
                  </label>
                  <textarea
                    v-model="departmentBodyEn"
                    placeholder="Welcome! Please select the department you would like to connect with:"
                    rows="3"
                    class="modal-textarea"
                  ></textarea>
                </div>
              </div>

              <!-- Right Panel: Preview -->
              <div class="preview-panel">
                <h4 class="preview-title">Preview</h4>
                <div class="whatsapp-preview">
                  <div class="preview-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.570-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.525 3.487"/>
                    </svg>
                    <span>WhatsApp Interactive Message</span>
                  </div>
                  <div class="preview-message">
                    <!-- Header Preview -->
                    <div v-if="departmentHeaderType === 'text'" class="preview-text-header">
                      {{ departmentHeaderEn || 'Select Department' }}
                    </div>
                    <div v-else-if="departmentHeaderType === 'image' && (previewMediaUrl || departmentHeaderMediaUrl)" class="preview-media-header">
                      <img :src="previewMediaUrl || getFullMediaUrl()" alt="Header Image" />
                    </div>
                    <div v-else-if="departmentHeaderType === 'video' && (previewMediaUrl || departmentHeaderMediaUrl)" class="preview-media-header">
                      <video :src="previewMediaUrl || getFullMediaUrl()" controls></video>
                    </div>
                    <div v-else-if="departmentHeaderType === 'document' && (departmentHeaderMediaUrl || selectedMediaFile)" class="preview-media-header">
                      <div class="document-preview">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                        </svg>
                        <span>PDF Document</span>
                      </div>
                    </div>
                    <!-- Body Preview -->
                    <div class="preview-body">
                      {{ departmentBodyEn || 'Welcome! Please select the department you would like to connect with:' }}
                    </div>
                    <!-- Button Preview -->
                    <button class="preview-list-button">View Options</button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-actions">
              <button @click="closeDepartmentSettingsModal" class="modal-btn cancel-btn">
                Cancel
              </button>
              <button @click="saveDepartmentSettingsFromModal" class="modal-btn confirm-btn" :disabled="departmentModeLoading || uploadInProgress">
                <span v-if="departmentModeLoading || uploadInProgress" class="loading-spinner-small"></span>
                {{ (departmentModeLoading || uploadInProgress) ? 'Saving...' : 'Save Settings' }}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Floating WhatsApp Button (Mobile + Desktop with Unread Messages) -->
    <FloatingWhatsAppButton />

    <!-- Floating Messenger Button (shown when connected + mobile or unread > 0) -->
    <FloatingMessengerButton />

    <!-- Notification Toast -->
    <div v-if="notification" class="notification-container" :class="notification.type">
      <div class="notification-content">
        <div class="notification-icon">
          <span v-if="notification.type === 'success'">✓</span>
          <span v-else-if="notification.type === 'error'">✕</span>
          <span v-else>ℹ</span>
        </div>
        <div class="notification-text">
          <div class="notification-title">{{ notification.title }}</div>
          <div class="notification-message">{{ notification.message }}</div>
        </div>
        <button @click="dismissNotification" class="notification-close">×</button>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from './Sidebar.vue'
import TopHeader from './TopHeader.vue'
import WhatsAppSetupModal from './WhatsAppSetupModal.vue'
import FloatingWhatsAppButton from './FloatingWhatsAppButton.vue'
import FloatingMessengerButton from './FloatingMessengerButton.vue'
import { authStore } from '@/store/auth'
import { AuthService } from '@/services/auth'
import { DashboardService } from '@/services/dashboard'
import { WhatsAppService } from '@/services/whatsapp'
import { CRMService } from '@/services/crmService'
import { OdooService } from '@/services/odooService'

export default {
  name: 'DashboardLayout',
  components: {
    Sidebar,
    TopHeader,
    WhatsAppSetupModal,
    FloatingWhatsAppButton,
    FloatingMessengerButton
  },
  data () {
    return {
      authStore, // Make authStore available to template
      dashboardData: {
        topCustomerService: null,
        topSales: null,
        qualiBotStatus: null,
        activeAgents: null,
        loading: true,
        error: null
      },
      overviewData: {
        success: false,
        loading: false,
        overall_statistics: {},
        satisfaction_distribution: [],
        top_performing_agents: [],
        most_common_issues: [],
        critical_analyses: [],
        risk_analysis: {},
        overview_period: {}
      },
      selectedTimeframe: '',
      selectedAnalysisType: '',
      appointmentMode: false,
      restaurantMode: false,
      customFlowMode: false,
      customFlowActiveId: null,
      customFlowName: '',
      modeLoading: false,
      modeStatus: 'disabled',
      modeExtraInfo: '',
      showConfirmModal: false,
      pendingMode: null,
      // Department Mode
      isDepartmentMode: false,
      departmentModeLoading: false,
      showDepartmentSettingsModal: false,
      showDepartmentWarningModal: false,
      pendingDepartmentModeValue: false,
      departmentTimeoutHours: 24,
      departmentHeaderAr: 'اختر القسم',
      departmentHeaderEn: 'Select Department',
      departmentBodyAr: 'مرحباً! من فضلك اختر القسم الذي تريد التواصل معه:',
      departmentBodyEn: 'Welcome! Please select the department you would like to connect with:',
      departmentHeaderType: 'text',
      departmentHeaderMediaUrl: null,
      selectedMediaFile: null,
      previewMediaUrl: null,
      uploadError: null,
      uploadInProgress: false,
      notification: null,
      qualiBotLoading: false,
      showQualiBotConfirmModal: false,
      whatsappStatus: {
        status: 'Unknown',
        uniqueSessionId: null,
        setupMethod: null, // 'manual' or 'embedded_signup'
        loading: false,
        error: null
      },
      whatsappStatusLoading: false,
      whatsappInitializeLoading: false,
      hasWhatsAppCredentials: null, // null = checking, true = has credentials, false = no credentials
      showWhatsAppSetupModal: false,
      crmStatus: {
        connected: false,
        sync_enabled: false,
        auto_sync_calls: false,
        auto_sync_whatsapp: false,
        token_expires_at: null,
        token_expired: false,
        last_sync_at: null,
        zoho_domain: '.com',
        loading: false
      },
      odooStatus: {
        connected: false,
        connection_valid: false,
        sync_enabled: false,
        auto_sync_calls: false,
        auto_sync_whatsapp: false,
        last_sync_at: null,
        odoo_url: '',
        odoo_database: '',
        loading: false
      },
      activeTab: 'online',
      whatsappInsights: {
        loading: false,
        error: null,
        date: null,
        todays_conversations: 0,
        yesterdays_conversations: 0,
        week_conversations: 0,
        month_conversations: 0,
        change_from_yesterday: {
          absolute: 0,
          percentage: 0
        }
      },
      leadData: {
        todayStats: null,
        weekStats: null,
        monthStats: null,
        allTimeStats: null,
        recentCompletions: [],
        motivationalMessage: null,
        loading: true,
        error: null
      },
      productsServicesCheck: {
        has_data: false,
        count: 0,
        loading: false,
        checked: false
      },
      accountDetails: {
        phone_verified_name: null,
        account_review_status: 'UNKNOWN',
        business_verification_status: 'UNKNOWN',
        messaging_limit: 'UNKNOWN',
        last_updated: null
      }
    }
  },
  async mounted () {
    // Check if we just came from a successful CRM connection
    if (localStorage.getItem('crm_connection_success')) {
      localStorage.removeItem('crm_connection_success')
      localStorage.removeItem('crm_connection_time')
      // Reload CRM status to show updated connection
      setTimeout(() => {
        this.loadCRMStatus()
      }, 1000)
    }

    await Promise.all([
      this.loadDashboardData(),
      this.checkCurrentMode(),
      this.loadOverviewData(),
      this.loadLeadStats(),
      this.loadWhatsAppQuickStatus(), // Single call: credentials + connection status + account details
      this.loadCRMStatus(),
      this.loadOdooStatus(),
      this.checkProductsServices(), // Check if user has products/services data
      this.loadDepartmentModeSettings() // Load department mode settings
    ])
    // Load insights after dashboard data is loaded
    await this.loadWhatsAppInsights()
  },
  methods: {
    async loadDashboardData () {
      try {
        this.dashboardData.loading = true
        this.dashboardData.error = null

        const data = await DashboardService.getAllDashboardData()

        this.dashboardData.topCustomerService = data.topCustomerService
        this.dashboardData.topSales = data.topSales
        this.dashboardData.qualiBotStatus = data.qualiBotStatus
        this.dashboardData.activeAgents = data.activeAgents
        this.dashboardData.lastUpdated = data.lastUpdated

        if (data.error) {
          this.dashboardData.error = data.error
          console.error('Dashboard data error:', data.error)
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error)
        this.dashboardData.error = error.message
        // Set fallback data
        this.dashboardData.topCustomerService = { bestPerformerName: 'Error loading' }
        this.dashboardData.topSales = { bestPerformerName: 'Error loading' }
        this.dashboardData.qualiBotStatus = { qualibotEnabled: false, status: 'ERROR', colorCode: '#ef4444' }
        this.dashboardData.activeAgents = { activeAgentsCount: 0, offlineAgentsCount: 0, totalAgents: 0 }
      } finally {
        this.dashboardData.loading = false
      }
    },

    async checkCurrentMode () {
      try {
        this.modeLoading = true

        const result = await DashboardService.checkCurrentMode()

        this.appointmentMode = result.mode === 'appointment'
        this.restaurantMode = result.mode === 'restaurant'
        this.customFlowMode = result.mode === 'custom_flow'
        this.customFlowActiveId = result.customFlowActiveId || null
        this.customFlowName = result.customFlowName || ''

        authStore.setAppointmentMode(this.appointmentMode)
        authStore.setRestaurantMode(this.restaurantMode)
        authStore.setCustomFlowMode(this.customFlowMode, this.customFlowActiveId)

        this.modeStatus = this.customFlowMode ? 'flow' : (this.restaurantMode ? 'restaurant' : (this.appointmentMode ? 'appointment' : 'customer_service'))
      } catch (error) {
        console.error('Error checking modes:', error)
        this.appointmentMode = false
        this.restaurantMode = false
        this.customFlowMode = false
        this.modeStatus = 'disabled'
      } finally {
        this.modeLoading = false
      }
    },

    async switchToCustomerService () {
      if (this.modeLoading || (!this.appointmentMode && !this.restaurantMode && !this.customFlowMode)) return
      this.showModeConfirmation('customer')
    },

    async switchToAppointmentMode () {
      if (this.modeLoading || this.appointmentMode) return
      this.showModeConfirmation('appointment')
    },

    async switchToRestaurantMode () {
      if (this.modeLoading || this.restaurantMode) return
      this.showModeConfirmation('restaurant')
    },

    async switchToCustomFlowMode () {
      if (this.modeLoading || this.customFlowMode) return
      if (!this.customFlowActiveId) {
        this.$router.push('/custom-flows')
        return
      }
      this.showModeConfirmation('flow')
    },

    showModeConfirmation (mode) {
      this.pendingMode = mode
      this.showConfirmModal = true
    },

    closeConfirmModal () {
      this.showConfirmModal = false
      this.pendingMode = null
    },

    async confirmModeSwitch () {
      if (this.modeLoading) return

      try {
        await this.toggleMode()
        this.closeConfirmModal()
      } catch (error) {
        // Error handling is already done in toggleMode
        this.closeConfirmModal()
      }
    },

    // Department Mode Methods
    confirmDepartmentModeToggle () {
      if (this.departmentModeLoading) return

      // v-model changes the value BEFORE @change is triggered
      // So if isDepartmentMode is TRUE now, it means user is ENABLING it
      // If isDepartmentMode is FALSE now, it means user is DISABLING it
      if (this.isDepartmentMode) {
        // User is ENABLING department mode - show warning about AI chatbot
        this.pendingDepartmentModeValue = true
        this.showDepartmentWarningModal = true
      } else {
        // User is DISABLING department mode - proceed directly without warning
        this.toggleDepartmentMode()
      }
    },

    cancelDepartmentModeToggle () {
      // Revert the toggle to the opposite of what it currently is
      this.isDepartmentMode = !this.isDepartmentMode
      this.showDepartmentWarningModal = false
      this.pendingDepartmentModeValue = false
    },

    proceedWithDepartmentModeToggle () {
      this.showDepartmentWarningModal = false
      this.toggleDepartmentMode()
    },

    async toggleDepartmentMode () {
      if (this.departmentModeLoading) return

      this.departmentModeLoading = true

      try {
        const response = await DashboardService.updateDepartmentModeSettings({
          customer_service_department_mode: this.isDepartmentMode ? 1 : 0,
          department_timeout_hours: this.departmentTimeoutHours,
          department_selection_header_ar: this.departmentHeaderAr,
          department_selection_header_en: this.departmentHeaderEn,
          department_selection_body_ar: this.departmentBodyAr,
          department_selection_body_en: this.departmentBodyEn
        })

        if (response.success) {
          // Show success notification
          this.showNotification({
            type: 'success',
            title: this.isDepartmentMode ? 'Department Routing Enabled' : 'Department Routing Disabled',
            message: response.message || (this.isDepartmentMode
              ? 'AI chatbot is now disabled. All messages will be routed to agents.'
              : 'Department routing disabled. System returned to normal operation.')
          })
        } else {
          // Handle failed response (like plan restrictions)
          this.isDepartmentMode = !this.isDepartmentMode
          this.showNotification({
            type: 'error',
            title: response.plan_restricted ? 'Upgrade Required' : 'Failed to Update',
            message: response.message || 'Could not update department mode settings.'
          })
        }
      } catch (error) {
        console.error('Error toggling department mode:', error)
        // Revert the toggle
        this.isDepartmentMode = !this.isDepartmentMode

        // Show error notification
        this.showNotification({
          type: 'error',
          title: 'Connection Error',
          message: error.message || 'Could not connect to server. Please try again.'
        })
      } finally {
        this.departmentModeLoading = false
        this.pendingDepartmentModeValue = false
      }
    },

    async openDepartmentSettingsModal () {
      // Load current settings before opening modal
      await this.loadDepartmentModeSettings()
      this.showDepartmentSettingsModal = true
    },

    closeDepartmentSettingsModal () {
      this.showDepartmentSettingsModal = false
      this.selectedMediaFile = null
      this.previewMediaUrl = null
      this.uploadError = null
    },

    getAcceptedFileTypes () {
      const types = {
        image: 'image/jpeg,image/png',
        video: 'video/mp4,video/3gpp',
        document: 'application/pdf'
      }
      return types[this.departmentHeaderType] || '*'
    },

    getMaxFileSize () {
      const sizes = {
        image: '5MB',
        video: '16MB',
        document: '100MB'
      }
      return sizes[this.departmentHeaderType] || '5MB'
    },

    getMediaFileName () {
      if (!this.departmentHeaderMediaUrl) return ''
      return this.departmentHeaderMediaUrl.split('/').pop()
    },

    getFullMediaUrl () {
      if (!this.departmentHeaderMediaUrl) return ''
      // If it's already a full URL, return it
      if (this.departmentHeaderMediaUrl.startsWith('http')) {
        return this.departmentHeaderMediaUrl
      }
      // Otherwise, construct full URL
      return window.location.origin + this.departmentHeaderMediaUrl
    },

    onHeaderTypeChange () {
      // Clear media when switching to text
      if (this.departmentHeaderType === 'text') {
        this.selectedMediaFile = null
        this.previewMediaUrl = null
        this.uploadError = null
      }
    },

    handleMediaFileChange (event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadError = null
      this.selectedMediaFile = file

      // Validate file size
      const maxSizes = {
        image: 5 * 1024 * 1024,
        video: 16 * 1024 * 1024,
        document: 100 * 1024 * 1024
      }

      const maxSize = maxSizes[this.departmentHeaderType]
      if (file.size > maxSize) {
        this.uploadError = `File size exceeds ${this.getMaxFileSize()} limit`
        this.selectedMediaFile = null
        return
      }

      // Create preview for images and videos
      if (this.departmentHeaderType === 'image' || this.departmentHeaderType === 'video') {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.previewMediaUrl = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },

    async deleteMedia () {
      if (!confirm('Are you sure you want to delete this media?')) return

      try {
        // If it's a newly selected file (not yet uploaded), just clear it
        if (this.selectedMediaFile && !this.departmentHeaderMediaUrl) {
          this.selectedMediaFile = null
          this.previewMediaUrl = null
          if (this.$refs.mediaFileInput) {
            this.$refs.mediaFileInput.value = ''
          }
          return
        }

        // If it's an existing uploaded file, delete from server
        if (this.departmentHeaderMediaUrl) {
          const response = await DashboardService.deleteDepartmentMedia()
          if (response.success) {
            this.departmentHeaderMediaUrl = null
            this.departmentHeaderType = 'text'
            this.selectedMediaFile = null
            this.previewMediaUrl = null
            if (this.$refs.mediaFileInput) {
              this.$refs.mediaFileInput.value = ''
            }
            this.showNotification({
              type: 'success',
              title: 'Media Deleted',
              message: 'Header media has been deleted successfully'
            })
          }
        }
      } catch (error) {
        console.error('Error deleting media:', error)
        this.showNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete media: ' + error.message
        })
      }
    },

    showNotification ({ type, title, message }) {
      this.notification = { type, title, message }

      // Auto-dismiss after 5 seconds
      setTimeout(() => {
        this.dismissNotification()
      }, 5000)
    },

    dismissNotification () {
      this.notification = null
    },

    async saveDepartmentSettingsFromModal () {
      if (!this.isDepartmentMode) return

      // Validation: Check if required media is present when header type is not 'text'
      if (this.departmentHeaderType !== 'text') {
        if (!this.selectedMediaFile && !this.departmentHeaderMediaUrl) {
          this.uploadError = `Please upload a ${this.departmentHeaderType} file or change header type to "Text Only"`
          this.showNotification({
            type: 'error',
            title: 'Missing Media',
            message: `Please upload a ${this.departmentHeaderType} file before saving.`
          })
          return
        }

        // Validation: Check file size if a new file is selected
        if (this.selectedMediaFile) {
          const maxSizes = {
            image: 5 * 1024 * 1024, // 5 MB
            video: 16 * 1024 * 1024, // 16 MB
            document: 100 * 1024 * 1024 // 100 MB
          }
          const maxSize = maxSizes[this.departmentHeaderType]

          if (this.selectedMediaFile.size > maxSize) {
            const maxSizeMB = maxSize / (1024 * 1024)
            this.uploadError = `File size exceeds the maximum allowed size of ${maxSizeMB} MB`
            this.showNotification({
              type: 'error',
              title: 'File Too Large',
              message: `${this.departmentHeaderType} file size must be less than ${maxSizeMB} MB`
            })
            return
          }
        }
      }

      // Validation: Check required text fields
      if (!this.departmentBodyAr || !this.departmentBodyEn) {
        this.uploadError = 'Please fill in both Arabic and English message body'
        this.showNotification({
          type: 'error',
          title: 'Missing Information',
          message: 'Please fill in both Arabic and English message body.'
        })
        return
      }

      if (this.departmentHeaderType === 'text') {
        if (!this.departmentHeaderAr || !this.departmentHeaderEn) {
          this.uploadError = 'Please fill in both Arabic and English headers'
          this.showNotification({
            type: 'error',
            title: 'Missing Information',
            message: 'Please fill in both Arabic and English headers.'
          })
          return
        }
      }

      this.departmentModeLoading = true
      this.uploadError = null

      try {
        let mediaUrl = this.departmentHeaderMediaUrl

        // Upload media file if one is selected
        if (this.selectedMediaFile) {
          this.uploadInProgress = true
          const uploadResponse = await DashboardService.uploadDepartmentMedia(
            this.selectedMediaFile,
            this.departmentHeaderType
          )

          if (uploadResponse.success) {
            mediaUrl = uploadResponse.media_url
          } else {
            throw new Error(uploadResponse.message || 'Failed to upload media')
          }
          this.uploadInProgress = false
        }

        const response = await DashboardService.updateDepartmentModeSettings({
          customer_service_department_mode: 1,
          department_timeout_hours: this.departmentTimeoutHours,
          department_selection_header_ar: this.departmentHeaderAr,
          department_selection_header_en: this.departmentHeaderEn,
          department_selection_body_ar: this.departmentBodyAr,
          department_selection_body_en: this.departmentBodyEn,
          department_selection_header_type: this.departmentHeaderType,
          department_selection_header_media_url: mediaUrl
        })

        if (response.success) {
          console.log('Department settings saved successfully')
          this.departmentHeaderMediaUrl = mediaUrl
          this.selectedMediaFile = null
          this.previewMediaUrl = null
          this.closeDepartmentSettingsModal()
          this.showNotification({
            type: 'success',
            title: 'Settings Saved',
            message: 'Department settings have been updated successfully!'
          })
        } else {
          throw new Error(response.message || 'Failed to save settings')
        }
      } catch (error) {
        console.error('Error saving department settings:', error)
        this.uploadError = error.message
        this.showNotification({
          type: 'error',
          title: 'Save Failed',
          message: error.message || 'Unknown error occurred'
        })
      } finally {
        this.departmentModeLoading = false
        this.uploadInProgress = false
      }
    },

    async loadDepartmentModeSettings () {
      try {
        const response = await DashboardService.getDepartmentModeSettings()
        console.log('[DEBUG] Department settings response:', response)
        if (response.success) {
          this.isDepartmentMode = response.customer_service_department_mode === 1
          this.departmentTimeoutHours = response.department_timeout_hours
          this.departmentHeaderAr = response.department_selection_header_ar
          this.departmentHeaderEn = response.department_selection_header_en
          this.departmentBodyAr = response.department_selection_body_ar
          this.departmentBodyEn = response.department_selection_body_en
          this.departmentHeaderType = response.department_selection_header_type || 'text'
          this.departmentHeaderMediaUrl = response.department_selection_header_media_url || null

          console.log('[DEBUG] Loaded header type:', this.departmentHeaderType)
          console.log('[DEBUG] Loaded media URL:', this.departmentHeaderMediaUrl)
        }
      } catch (error) {
        console.error('Error loading department mode settings:', error)
      }
    },

    async loadOverviewData () {
      try {
        this.overviewData.loading = true
        const params = {}

        if (this.selectedTimeframe) {
          const endDate = new Date()
          const startDate = new Date()
          startDate.setDate(endDate.getDate() - parseInt(this.selectedTimeframe))
          params.date_from = startDate.toISOString().split('T')[0]
          params.date_to = endDate.toISOString().split('T')[0]
        }

        if (this.selectedAnalysisType) {
          params.analysis_type = this.selectedAnalysisType
        }

        const data = await DashboardService.getAIAnalysisOverview(params)

        if (data.success) {
          this.overviewData = { ...data }
        } else {
          console.error('Error loading overview data:', data.error)
        }
      } catch (error) {
        console.error('Error loading overview data:', error)
      } finally {
        this.overviewData.loading = false
      }
    },

    async updateOverviewData () {
      await this.loadOverviewData()
    },

    formatPeriod (period) {
      if (!period?.date_from || !period?.date_to) return ''
      const from = new Date(period.date_from).toLocaleDateString()
      const to = new Date(period.date_to).toLocaleDateString()
      return `${from} - ${to}`
    },

    getSatisfactionRate () {
      const satisfied = this.overviewData.satisfaction_distribution?.find(
        item => item.customer_satisfaction_prediction === 'satisfied'
      )
      return satisfied ? Math.round(satisfied.percentage) : 0
    },

    formatSatisfactionLabel (prediction) {
      const labels = {
        satisfied: 'Satisfied',
        neutral: 'Neutral',
        dissatisfied: 'Dissatisfied'
      }
      return labels[prediction] || prediction
    },

    getSatisfactionClass (rate) {
      if (rate >= 80) return 'excellent'
      if (rate >= 60) return 'good'
      if (rate >= 40) return 'average'
      return 'poor'
    },

    async toggleMode () {
      if (this.modeLoading) return

      try {
        this.modeLoading = true

        const modeMap = { customer: 'customer_service', appointment: 'appointment', restaurant: 'restaurant', flow: 'custom_flow' }
        const targetMode = modeMap[this.pendingMode]

        const result = await DashboardService.setMode(targetMode)

        if (result.success) {
          this.appointmentMode = targetMode === 'appointment'
          this.restaurantMode = targetMode === 'restaurant'
          this.customFlowMode = targetMode === 'custom_flow'
          if (targetMode !== 'custom_flow') this.customFlowActiveId = null

          authStore.setAppointmentMode(this.appointmentMode)
          authStore.setRestaurantMode(this.restaurantMode)
          authStore.setCustomFlowMode(this.customFlowMode, this.customFlowActiveId)

          const modeLabels = {
            customer_service: 'Customer Service Mode',
            appointment: 'Appointment Mode',
            restaurant: 'Restaurant Mode',
            custom_flow: 'Custom Flow Mode'
          }
          this.showNotification({
            type: 'success',
            title: `${modeLabels[targetMode]} Enabled`,
            message: result.message || `Switched to ${modeLabels[targetMode]}.`
          })
        } else {
          this.showNotification({
            type: 'error',
            title: 'Failed to Switch Mode',
            message: result.message || 'Could not update mode.'
          })
        }

        this.modeStatus = this.customFlowMode ? 'flow' : (this.restaurantMode ? 'restaurant' : (this.appointmentMode ? 'appointment' : 'customer_service'))
        this.closeConfirmModal()
      } catch (error) {
        console.error('Error switching mode:', error)
        this.showNotification({
          type: 'error',
          title: 'Error Switching Mode',
          message: error.message || 'An unexpected error occurred while switching modes.'
        })
        await this.checkCurrentMode()
      } finally {
        this.modeLoading = false
      }
    },

    toggleQualiBot () {
      this.showQualiBotConfirmModal = true
    },

    closeQualiBotConfirmModal () {
      this.showQualiBotConfirmModal = false
    },

    async confirmQualiBotToggle () {
      if (this.qualiBotLoading) return

      try {
        this.qualiBotLoading = true
        const token = authStore.getAuthToken?.() || localStorage.getItem('auth_token')

        if (!token) {
          throw new Error('No authentication token found')
        }

        const response = await fetch('https://api.quali-d.com/company-bot/toggle', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          mode: 'cors'
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()

        // Check for success based on message content
        if (result.message && (result.message.includes('turned on') || result.message.includes('turned off'))) {
          // Refresh dashboard data to get updated QualiBot status
          await this.loadDashboardData()
          // Reload insights based on new QualiBot status
          await this.loadWhatsAppInsights()
          console.log('QualiBot toggled successfully')
          this.closeQualiBotConfirmModal()
        } else {
          throw new Error(result.message || 'Failed to toggle QualiBot')
        }
      } catch (error) {
        console.error('Error toggling QualiBot:', error)
        // Optionally show user-friendly error message
      } finally {
        this.qualiBotLoading = false
      }
    },

    async loadWhatsAppInsights () {
      // Only load insights if WhatsApp is connected
      if (!this.hasWhatsAppCredentials) {
        return
      }

      try {
        this.whatsappInsights.loading = true
        this.whatsappInsights.error = null

        const data = await DashboardService.getTodaysConversations()

        if (data.success) {
          this.whatsappInsights = {
            loading: false,
            error: null,
            date: data.date,
            todays_conversations: data.todays_conversations || 0,
            yesterdays_conversations: data.yesterdays_conversations || 0,
            week_conversations: data.week_conversations || 0,
            month_conversations: data.month_conversations || 0,
            change_from_yesterday: data.change_from_yesterday || {
              absolute: 0,
              percentage: 0
            }
          }
        } else {
          this.whatsappInsights.error = data.error || 'Failed to load insights'
          this.whatsappInsights.loading = false
        }
      } catch (error) {
        console.error('Error loading WhatsApp insights:', error)
        this.whatsappInsights.error = 'Failed to load insights'
        this.whatsappInsights.loading = false
      }
    },

    formatDate (dateString) {
      if (!dateString) return 'N/A'
      try {
        return new Date(dateString).toLocaleDateString()
      } catch (error) {
        return dateString
      }
    },

    async loadLeadStats () {
      this.leadData.loading = true
      this.leadData.error = null

      const result = await DashboardService.getLeadDashboardStats()

      this.leadData.todayStats = result.todayStats
      this.leadData.weekStats = result.weekStats
      this.leadData.monthStats = result.monthStats
      this.leadData.allTimeStats = result.allTimeStats
      this.leadData.recentCompletions = result.recentCompletions
      this.leadData.motivationalMessage = result.motivationalMessage
      this.leadData.error = result.error
      this.leadData.loading = false
    },

    getTrendClass (trend) {
      if (!trend) return 'neutral'
      const value = parseInt(trend.replace(/[^-\d]/g, ''))
      return value > 0 ? 'positive' : value < 0 ? 'negative' : 'neutral'
    },

    formatTime (timestamp) {
      if (!timestamp) return 'Unknown'
      try {
        // Handle both ISO format and MySQL datetime format
        const date = new Date(timestamp.replace(' ', 'T'))
        const now = new Date()
        const diffMs = now - date
        const diffMins = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMins / 60)
        const diffDays = Math.floor(diffHours / 24)

        if (diffMins < 1) return 'Just now'
        if (diffMins < 60) return `${diffMins}m ago`
        if (diffHours < 24) return `${diffHours}h ago`
        if (diffDays < 30) return `${diffDays}d ago`
        return date.toLocaleDateString()
      } catch (error) {
        console.error('Error formatting time:', error)
        return timestamp
      }
    },

    async loadWhatsAppQuickStatus () {
      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          this.hasWhatsAppCredentials = false
          return
        }

        const response = await fetch('https://api.quali-d.com/whatsapp/quick-status', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (!response.ok) {
          const sessionId = localStorage.getItem('whatsapp_unique_session_id')
          this.hasWhatsAppCredentials = !!sessionId
          return
        }

        const data = await response.json()
        this.hasWhatsAppCredentials = data.has_credentials === true

        if (!data.has_credentials) {
          if (!this.hasUserDismissedSetupModal()) {
            this.showWhatsAppSetupModal = true
          }
          return
        }

        this.whatsappStatus = {
          status: data.connection_status || 'Unknown',
          uniqueSessionId: data.session_id || null,
          setupMethod: data.setup_method || 'manual',
          loading: false,
          error: null
        }

        this.accountDetails = {
          phone_verified_name: data.phone_verified_name,
          account_review_status: data.account_review_status,
          business_verification_status: data.business_verification_status,
          messaging_limit: data.messaging_limit,
          last_updated: data.last_updated
        }
      } catch (error) {
        console.error('Error loading WhatsApp quick status:', error)
        const sessionId = localStorage.getItem('whatsapp_unique_session_id')
        this.hasWhatsAppCredentials = !!sessionId
      }
    },

    // WhatsApp Credentials Check
    async checkWhatsAppCredentials () {
      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          this.hasWhatsAppCredentials = false
          return
        }

        const response = await fetch('https://api.quali-d.com/whatsapp/has-credentials', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        if (!response.ok) {
          // Fallback: if we can't check credentials but we have a session ID, assume credentials exist
          const sessionId = localStorage.getItem('whatsapp_unique_session_id')
          this.hasWhatsAppCredentials = !!sessionId
          return
        }

        const data = await response.json()

        this.hasWhatsAppCredentials = data.has_credentials === true

        if (data.has_credentials) {
          this.whatsappStatus.setupMethod = data.setup_method || 'manual'
        }

        // Show modal if no credentials and user hasn't dismissed it before
        if (!this.hasWhatsAppCredentials && !this.hasUserDismissedSetupModal()) {
          this.showWhatsAppSetupModal = true
        }
      } catch (error) {
        console.error('Error checking WhatsApp credentials:', error)
        const sessionId = localStorage.getItem('whatsapp_unique_session_id')
        this.hasWhatsAppCredentials = !!sessionId
      }
    },

    hasUserDismissedSetupModal () {
      return localStorage.getItem('whatsapp_setup_modal_dismissed') === 'true'
    },

    dismissWhatsAppSetupModal () {
      this.showWhatsAppSetupModal = false
      localStorage.setItem('whatsapp_setup_modal_dismissed', 'true')
    },

    startWhatsAppSetup () {
      this.showWhatsAppSetupModal = false
      this.$router.push('/tutorials/whatsapp')
    },

    // WhatsApp Status Methods
    async loadWhatsAppStatus () {
      try {
        this.whatsappStatus.loading = true
        this.whatsappStatus.error = null

        const result = await WhatsAppService.getConnectionStatus()

        this.whatsappStatus = {
          status: result.status || 'Unknown',
          uniqueSessionId: result.uniqueSessionId || null,
          loading: false,
          error: result.success ? null : result.error
        }

        console.log('WhatsApp status loaded:', this.whatsappStatus)
      } catch (error) {
        console.error('Error loading WhatsApp status:', error)
        this.whatsappStatus = {
          status: 'Error',
          uniqueSessionId: null,
          loading: false,
          error: error.message
        }
      }
    },

    async recheckWhatsAppStatus () {
      if (this.whatsappStatusLoading) return

      try {
        this.whatsappStatusLoading = true

        const result = await WhatsAppService.refreshConnectionStatus()

        this.whatsappStatus = {
          status: result.status || 'Unknown',
          uniqueSessionId: result.uniqueSessionId || null,
          loading: false,
          error: result.success ? null : result.error
        }

        console.log('WhatsApp status rechecked:', this.whatsappStatus)
      } catch (error) {
        console.error('Error rechecking WhatsApp status:', error)
        this.whatsappStatus.error = error.message
      } finally {
        this.whatsappStatusLoading = false
      }
    },

    getStatusColor (status) {
      return WhatsAppService.getStatusColor(status)
    },

    getStatusIcon (status) {
      return WhatsAppService.getStatusIcon(status)
    },

    async initializeWhatsApp () {
      if (this.whatsappInitializeLoading) return

      try {
        this.whatsappInitializeLoading = true

        const result = await WhatsAppService.initializeConnection()

        if (result.success) {
          console.log('WhatsApp initialized successfully:', result.message)

          // Update status to connecting while initialization is processing
          this.whatsappStatus.status = 'Connecting'

          // Wait a moment then recheck status to see if it connected
          setTimeout(async () => {
            await this.recheckWhatsAppStatus()
          }, 3000)
        } else {
          console.error('Failed to initialize WhatsApp:', result.error)
          this.whatsappStatus.error = result.error
        }
      } catch (error) {
        console.error('Error initializing WhatsApp:', error)
        this.whatsappStatus.error = error.message
      } finally {
        this.whatsappInitializeLoading = false
      }
    },

    async loadAccountDetails () {
      try {
        const token = AuthService.getAuthToken()
        if (!token) {
          console.log('No auth token available for account details')
          return
        }

        const response = await fetch('https://api.quali-d.com/whatsapp-account-details', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const data = await response.json()

        if (data.success && data.account_details) {
          this.accountDetails = {
            phone_verified_name: data.account_details.phone_verified_name,
            account_review_status: data.account_details.account_review_status,
            business_verification_status: data.account_details.business_verification_status,
            messaging_limit: data.account_details.messaging_limit,
            last_updated: data.account_details.last_updated
          }
          console.log('Account details loaded:', this.accountDetails)
        }
      } catch (error) {
        console.error('Error loading account details:', error)
      }
    },

    async loadCRMStatus () {
      try {
        this.crmStatus.loading = true
        const status = await CRMService.getStatus()
        this.crmStatus = { ...this.crmStatus, ...status, loading: false }
        console.log('CRM status loaded:', this.crmStatus)
      } catch (error) {
        console.error('Error loading CRM status:', error)
        this.crmStatus = {
          ...this.crmStatus,
          connected: false,
          loading: false,
          error: error.message
        }
      }
    },

    async loadOdooStatus () {
      try {
        this.odooStatus.loading = true
        const status = await OdooService.getStatus()
        this.odooStatus = { ...this.odooStatus, ...status, loading: false }
        console.log('Odoo status loaded:', this.odooStatus)
      } catch (error) {
        console.error('Error loading Odoo status:', error)
        this.odooStatus = {
          ...this.odooStatus,
          connected: false,
          loading: false,
          error: error.message
        }
      }
    },

    formatLastSync (timestamp) {
      return CRMService.formatLastSync(timestamp)
    },

    async checkProductsServices () {
      try {
        this.productsServicesCheck.loading = true
        const token = AuthService.getAuthToken()

        if (!token) {
          throw new Error('No authentication token found')
        }

        const response = await fetch('https://api.quali-d.com/products-services/check', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          mode: 'cors'
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.success) {
          this.productsServicesCheck.has_data = data.has_data
          this.productsServicesCheck.count = data.count
          this.productsServicesCheck.checked = true
        }
      } catch (error) {
        console.error('Error checking products/services:', error)
        this.productsServicesCheck.checked = true
      } finally {
        this.productsServicesCheck.loading = false
      }
    },

    // Account Details Helper Methods
    formatLastUpdated (timestamp) {
      if (!timestamp) return 'Never'

      const date = new Date(timestamp)
      const now = new Date()
      const diffMs = now - date
      const diffMins = Math.floor(diffMs / 60000)
      const diffHours = Math.floor(diffMs / 3600000)
      const diffDays = Math.floor(diffMs / 86400000)

      if (diffMins < 1) return 'Just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays < 7) return `${diffDays}d ago`

      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },

    getReviewStatusClass (status) {
      const statusMap = {
        APPROVED: 'status-approved',
        PENDING: 'status-pending',
        REJECTED: 'status-rejected',
        UNKNOWN: 'status-unknown'
      }
      return statusMap[status] || 'status-unknown'
    },

    formatReviewStatus (status) {
      const statusMap = {
        APPROVED: 'Account Approved',
        PENDING: 'Under Review',
        REJECTED: 'Account Rejected',
        UNKNOWN: 'Status Unknown'
      }
      return statusMap[status] || status
    },

    getVerificationStatusClass (status) {
      const statusMap = {
        verified: 'status-approved',
        unverified: 'status-pending',
        UNKNOWN: 'status-unknown'
      }
      return statusMap[status] || 'status-unknown'
    },

    formatVerificationStatus (status) {
      const statusMap = {
        verified: 'Business Verified',
        unverified: 'Not Verified',
        UNKNOWN: 'Verification Unknown'
      }
      return statusMap[status] || status
    },

    getMessagingLimitTier (limit) {
      const tierMap = {
        TIER_250: '250',
        TIER_2K: '2k',
        TIER_10K: '10k',
        TIER_100K: '100k',
        TIER_UNLIMITED: 'unlimited',
        UNKNOWN: 'unknown'
      }
      return tierMap[limit] || 'unknown'
    },

    formatMessagingLimit (limit) {
      const limitMap = {
        TIER_250: '250 msgs/day',
        TIER_2K: '2K msgs/day',
        TIER_10K: '10K msgs/day',
        TIER_100K: '100K msgs/day',
        TIER_UNLIMITED: 'Unlimited Messages',
        UNKNOWN: 'Limit Unknown'
      }
      return limitMap[limit] || limit
    }
  },
  computed: {
    welcomeMessage () {
      const hour = new Date().getHours()
      const userName = authStore.user?.name || 'User'

      if (hour < 12) {
        return `Good morning, ${userName}!`
      } else if (hour < 17) {
        return `Good afternoon, ${userName}!`
      } else {
        return `Good evening, ${userName}!`
      }
    },
    companyName () {
      return authStore.user?.company || 'your business'
    },
    userPlan () {
      return authStore.user?.plan || 'No Plan'
    },
    hasCRMAccess () {
      // CRM integrations are only available for Starter and Pro plans
      const planName = authStore.user?.plan
      console.log('🔍 CRM Access Check:', {
        planName: planName,
        user: authStore.user
      })
      // Check if plan name includes "Starter" or "Pro"
      if (!planName) return false
      return planName.includes('Starter') || planName.includes('Pro')
    },
    formattedCredits () {
      if (authStore.creditsLoading) {
        return 'Loading...'
      }
      return authStore.credits.toLocaleString()
    },
    isAppointmentMode () {
      return this.appointmentMode
    },
    isRestaurantMode () {
      return this.restaurantMode
    },
    isCustomFlowMode () {
      return this.customFlowMode
    },
    currentModeName () {
      if (this.customFlowMode) return 'Custom Flow Mode'
      if (this.restaurantMode) return 'Restaurant Mode'
      return this.appointmentMode ? 'Appointment Mode' : 'Customer Service'
    },
    crmStatusText () {
      if (this.crmStatus.token_expired) return 'TOKEN EXPIRED'
      return this.crmStatus.connected ? 'CONNECTED' : 'NOT CONNECTED'
    },
    odooStatusText () {
      if (this.odooStatus.connected && !this.odooStatus.connection_valid) return 'CONNECTION INVALID'
      return this.odooStatus.connected ? 'CONNECTED' : 'NOT CONNECTED'
    },
    shouldShowProductsServicesWarning () {
      // Only show warning in Customer Service mode when products/services are not set
      return !this.appointmentMode &&
             this.productsServicesCheck.checked &&
             !this.productsServicesCheck.has_data
    }
  }
}
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
  overflow-x: visible;
}

.page-content {
  flex: 1;
  padding: 32px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
  overflow-x: visible;
}

.welcome-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.welcome-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.welcome-content p {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.quick-stats {
  display: flex;
  gap: 32px;
}

.stat-item {
  text-align: right;
}

/* Enhanced Mode Switcher */
.mode-switcher-enhanced {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg-secondary);
  border-radius: 16px;
  padding: 20px 24px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.mode-status {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mode-icon {
  font-size: 20px;
  transition: all 0.3s ease;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.mode-icon:hover {
  transform: scale(1.1);
}

.mode-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-right: 24px;
}

.mode-description {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.current-mode {
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.mode-toggle {
  display: flex;
  background: var(--bg-primary);
  border-radius: 12px;
  padding: 4px;
  box-shadow: inset 0 2px 4px var(--shadow-secondary);
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.toggle-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.toggle-btn:not(.active):hover:not(:disabled) {
  color: var(--text-primary);
  background: var(--bg-secondary);
}

.toggle-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toggle-btn-flow.active {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  box-shadow: 0 2px 8px rgba(139, 92, 246, 0.4);
  display: flex;
  align-items: center;
  gap: 6px;
}

.toggle-btn-flow:not(.active):hover:not(:disabled) {
  color: #7c3aed;
  background: rgba(139, 92, 246, 0.08);
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
  animation: pulse-dot 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

/* Department Mode Inline (inside Current Mode section) */
.department-mode-inline {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-primary);
}

.department-label-inline {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
}

/* Settings Icon Button */
.settings-icon-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-secondary);
}

.settings-icon-btn:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  transform: scale(1.05);
}

.settings-icon-btn:active {
  transform: scale(0.95);
}

/* Compact Toggle Switch */
.switch-compact {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch-compact input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider-compact {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--bg-tertiary);
  transition: 0.3s;
  border-radius: 24px;
  border: 2px solid var(--border-primary);
}

.slider-compact:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

input:checked + .slider-compact {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-color: #3b82f6;
}

input:checked + .slider-compact:before {
  transform: translateX(20px);
}

input:disabled + .slider-compact {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Smaller Compact Toggle Switch for Inline Department Mode */
.switch-compact-small {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}

.switch-compact-small input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider-compact-small {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--bg-tertiary);
  transition: 0.3s;
  border-radius: 20px;
  border: 2px solid var(--border-primary);
}

.slider-compact-small:before {
  position: absolute;
  content: "";
  height: 12px;
  width: 12px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

input:checked + .slider-compact-small {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-color: #3b82f6;
}

input:checked + .slider-compact-small:before {
  transform: translateX(16px);
}

input:disabled + .slider-compact-small {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Smaller Settings Icon Button */
.settings-icon-btn-small {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-secondary);
}

.settings-icon-btn-small:hover {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  transform: scale(1.05);
}

.settings-icon-btn-small:active {
  transform: scale(0.95);
}

/* Controls Container for Inline Department Mode */
.department-controls-inline {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Department Settings Modal */
.department-settings-modal {
  background: var(--bg-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.department-settings-modal-wide {
  position: relative;
  background: var(--bg-primary);
  border-radius: 16px;
  width: 95%;
  max-width: 1200px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.department-settings-modal-wide .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-primary);
  position: relative;
}

.department-settings-modal-wide .modal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
  text-align: left;
}

.department-settings-modal-wide .modal-close-btn {
  position: relative;
  top: 0;
  right: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.department-settings-modal-wide .modal-close-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-body-split {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.settings-panel {
  overflow-y: auto;
  padding-right: 16px;
}

.preview-panel {
  position: sticky;
  top: 0;
  height: fit-content;
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-primary);
}

.preview-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.whatsapp-preview {
  background: #e5ddd5;
  border-radius: 12px;
  padding: 16px;
  max-width: 100%;
}

.preview-header {
  background: #128c7e;
  color: white;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  margin: -16px -16px 16px -16px;
}

.preview-message {
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-text-header {
  font-weight: 700;
  font-size: 15px;
  color: #1f1f1f;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.preview-media-header {
  margin-bottom: 12px;
  border-radius: 8px;
  overflow: hidden;
}

.preview-media-header img,
.preview-media-header video {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.document-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #6c757d;
  gap: 8px;
}

.preview-body {
  font-size: 14px;
  color: #1f1f1f;
  line-height: 1.5;
  margin-bottom: 12px;
}

.preview-list-button {
  width: 100%;
  padding: 10px;
  background: #128c7e;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: default;
}

.file-upload-area {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-secondary);
  border: 2px dashed var(--border-primary);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.file-upload-area:hover {
  border-color: #3b82f6;
  background: var(--bg-tertiary);
}

.file-input-hidden {
  display: none;
}

.file-upload-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.file-upload-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.file-name {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-placeholder {
  font-size: 13px;
  color: var(--text-tertiary);
  flex: 1;
}

.delete-media-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #dc2626;
  color: white;
  border: none;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.delete-media-btn:hover {
  background: #b91c1c;
  transform: scale(1.1);
}

.file-size-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 4px;
}

.file-requirements {
  margin-top: 12px;
  padding: 12px;
  background: #f0f9ff;
  border-left: 3px solid #3b82f6;
  border-radius: 6px;
}

.file-requirements .requirement-item strong {
  display: block;
  color: #1e40af;
  font-size: 13px;
  margin-bottom: 6px;
}

.file-requirements ul {
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
}

.file-requirements li {
  font-size: 12px;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 3px;
}

.upload-error {
  color: #dc2626;
  font-size: 13px;
  margin-top: 8px;
  font-weight: 500;
}

@media (max-width: 1024px) {
  .department-settings-modal-wide {
    max-width: 95%;
  }

  .modal-body-split {
    grid-template-columns: 1fr;
  }

  .preview-panel {
    position: relative;
  }
}

.modal-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 28px;
  color: var(--text-secondary);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-setting-row {
  margin-bottom: 20px;
}

.modal-setting-row:last-child {
  margin-bottom: 0;
}

.modal-setting-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.modal-setting-label svg {
  flex-shrink: 0;
  opacity: 0.7;
}

.modal-select,
.modal-input,
.modal-textarea {
  width: 100%;
  padding: 10px 14px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.modal-select {
  cursor: pointer;
}

.modal-textarea {
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
}

.modal-select:hover,
.modal-input:hover,
.modal-textarea:hover {
  border-color: #3b82f6;
}

.modal-select:focus,
.modal-input:focus,
.modal-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-input::placeholder,
.modal-textarea::placeholder {
  color: var(--text-tertiary);
  opacity: 0.6;
}

/* WhatsApp Quick Status Card */
.whatsapp-quick-status {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  margin-bottom: 24px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 2px solid var(--border-primary);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.whatsapp-quick-status::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #25D366 0%, #128C7E 100%);
}

.whatsapp-quick-status.connected {
  border-color: rgba(37, 211, 102, 0.3);
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.05) 0%, rgba(18, 140, 126, 0.03) 100%);
}

.whatsapp-quick-status.disconnected {
  border-color: rgba(239, 68, 68, 0.3);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.03) 100%);
}

.whatsapp-quick-status.disconnected::before {
  background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
}

.quick-status-icon {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(37, 211, 102, 0.3);
}

.whatsapp-quick-status.disconnected .quick-status-icon {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

.quick-status-icon svg {
  color: white;
}

.status-pulse {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: #22c55e;
  border-radius: 50%;
  border: 2px solid var(--bg-primary);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.quick-status-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quick-status-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.upgrade-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #78350f;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  animation: subtle-pulse 3s ease-in-out infinite;
  box-shadow: 0 2px 4px rgba(251, 191, 36, 0.3);
}

@keyframes subtle-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.9;
    transform: scale(1.02);
  }
}

.quick-status-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.status-indicator {
  font-size: 13px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
  display: inline-block;
}

.status-indicator.connected {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.status-indicator.disconnected {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

/* Verified Name Inline */
.verified-name-inline {
  color: var(--text-tertiary);
  font-weight: 600;
  font-size: 13px;
}

/* Account Details Inline */
.account-details-inline {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-primary);
}

.account-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.status-item-inline {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-item-inline svg {
  opacity: 0.6;
  flex-shrink: 0;
}

.status-badge-small {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid;
}

.status-badge-small.status-approved {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.3);
}

.status-badge-small.status-pending {
  background: rgba(251, 191, 36, 0.1);
  color: #d97706;
  border-color: rgba(251, 191, 36, 0.3);
}

.status-badge-small.status-rejected {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.3);
}

.status-badge-small.status-unknown {
  background: rgba(156, 163, 175, 0.1);
  color: #6b7280;
  border-color: rgba(156, 163, 175, 0.3);
}

.status-badge-small.messaging-limit.limit-250 {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.3);
}

.status-badge-small.messaging-limit.limit-2k {
  background: rgba(251, 191, 36, 0.1);
  color: #d97706;
  border-color: rgba(251, 191, 36, 0.3);
}

.status-badge-small.messaging-limit.limit-10k,
.status-badge-small.messaging-limit.limit-100k {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
  border-color: rgba(59, 130, 246, 0.3);
}

.status-badge-small.messaging-limit.limit-unlimited {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.3);
}

.status-badge-small.messaging-limit.limit-unknown {
  background: rgba(156, 163, 175, 0.1);
  color: #6b7280;
  border-color: rgba(156, 163, 175, 0.3);
}

/* Products/Services Warning Banner */
.products-services-warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 6px -1px rgba(245, 158, 11, 0.1), 0 2px 4px -1px rgba(245, 158, 11, 0.06);
  animation: slideDown 0.4s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.warning-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-size: 16px;
  font-weight: 700;
  color: #92400e;
  margin: 0 0 6px 0;
}

.warning-message {
  font-size: 14px;
  color: #78350f;
  margin: 0;
  line-height: 1.5;
}

.warning-action-btn {
  background: #f59e0b;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
}

.warning-action-btn:hover {
  background: #d97706;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(245, 158, 11, 0.3);
}

.warning-action-btn:active {
  transform: translateY(0);
}

/* Confirmation Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.confirmation-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 420px;
  width: 90%;
  margin: 20px;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  padding: 24px 24px 16px;
  text-align: center;
}

.modal-icon {
  font-size: 48px;
  margin-bottom: 12px;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-body {
  padding: 0 24px 24px;
  text-align: center;
}

.modal-message {
  font-size: 16px;
  color: #374151;
  margin: 0 0 12px;
  line-height: 1.5;
}

.modal-submessage {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.modal-actions {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.modal-btn {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
  justify-content: center;
}

.cancel-btn {
  background: white;
  color: #6b7280;
  border-color: #d1d5db;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  color: #374151;
}

.confirm-btn {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.confirm-btn:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.confirm-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Confirm Modal Styles (for Warning Modal) */
.confirm-modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  margin: 20px;
  padding: 32px;
  animation: modalSlideIn 0.3s ease-out;
  border: 1px solid var(--border-primary);
}

.confirm-icon {
  font-size: 48px;
  margin-bottom: 16px;
  text-align: center;
}

.confirm-icon.warning {
  filter: drop-shadow(0 2px 4px rgba(245, 158, 11, 0.3));
}

.confirm-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 16px;
  text-align: center;
}

.confirm-message {
  font-size: 16px;
  color: var(--text-primary);
  margin: 0 0 12px;
  line-height: 1.6;
  text-align: center;
}

.confirm-submessage {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px;
  line-height: 1.5;
  text-align: center;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
}

.confirm-cancel-btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-primary);
  min-width: 100px;
}

.confirm-cancel-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
  transform: translateY(-1px);
}

.confirm-btn.warning {
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid #f59e0b;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  min-width: 100px;
}

.confirm-btn.warning:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  border-color: #d97706;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.confirm-btn.warning:active {
  transform: translateY(0);
}

/* Compact Performers Card */
.dashboard-card {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  color: var(--text-primary);
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px var(--shadow-primary);
  border-color: var(--border-secondary);
}

.performers-card-compact {
  padding: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.card-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  font-weight: 500;
}

.card-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-badge.gold {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
}

.performers-list-compact {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.performer-item-compact {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid var(--border-primary);
}

.performer-item-compact:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
  transform: translateX(2px);
}

.performer-avatar-compact {
  position: relative;
}

.performer-avatar-compact img {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.performer-rank-compact {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

.performer-info-compact {
  flex: 1;
}

.performer-name-compact {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.performer-role-compact {
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* QUALIBOT Hero Card Styles - Compact */
.qualibot-hero-card {
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;
  overflow: hidden;
  padding: 18px;
}

.qualibot-hero-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 18px;
  padding: 2px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6, #06b6d4);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

.qualibot-hero-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
}

.qualibot-visual-section {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;
}

.qualibot-hero-gif {
  width: 120px;
  height: 120px;
  object-fit: contain;
  filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.3));
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.qualibot-glow.active {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  background: radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%);
  border-radius: 50%;
  animation: pulse-glow 2s infinite;
  z-index: 1;
}

@keyframes pulse-glow {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.2); opacity: 1; }
}

.qualibot-info-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.qualibot-hero-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  line-height: 1;
}

.ai-label {
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

.qualibot-name {
  background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: 1px;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.1);
}

.qualibot-status-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-label {
  color: #94a3b8;
  font-size: 13px;
  font-weight: 500;
}

.status-value {
  color: #ef4444;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 1px 6px;
  border-radius: 6px;
  background: rgba(239, 68, 68, 0.1);
  transition: all 0.3s ease;
}

.status-value.connected {
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.2);
}

.qualibot-hero-toggle {
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border: 2px solid #4b5563;
  color: #d1d5db;
  padding: 12px 20px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.4s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.qualibot-hero-toggle::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.qualibot-hero-toggle:hover::before {
  left: 100%;
}

.qualibot-hero-toggle:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  border-color: #3b82f6;
}

.qualibot-hero-toggle:not(.active):hover {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
  color: white;
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

.qualibot-hero-toggle.active {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-color: #ef4444;
  color: white;
  box-shadow: 0 0 15px rgba(239, 68, 68, 0.4);
}

.qualibot-hero-toggle.active:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.6);
}

.toggle-icon {
  font-size: 14px;
  filter: drop-shadow(0 0 3px currentColor);
}

.toggle-text {
  font-weight: 700;
}

/* Tabbed Agents Card */
.agents-tabbed-card {
  background: var(--bg-primary);
  padding: 20px;
  transition: background-color 0.3s ease;
}

.agents-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.agents-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.agents-tabs {
  display: flex;
  gap: 4px;
  background: var(--bg-secondary);
  padding: 2px;
  border-radius: 8px;
  border: 1px solid var(--border-primary);
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.tab-btn {
  padding: 6px 12px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn.active {
  background: var(--bg-primary);
  color: var(--text-primary);
  box-shadow: 0 1px 3px var(--shadow-secondary);
}

.tab-btn:first-child.active {
  background: #10b981;
  color: white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.tab-btn:last-child.active {
  background: #ef4444;
  color: white;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.tab-btn:hover:not(.active) {
  color: var(--text-primary);
  background: var(--bg-tertiary);
}

.agents-tab-content {
  min-height: 60px;
}

.tab-panel {
  padding: 0;
}

.agents-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.agent-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border-left: 3px solid transparent;
  transition: background-color 0.3s ease;
}

.agent-item.online {
  border-left-color: #10b981;
  background: rgba(16, 185, 129, 0.03);
}

.agent-item.offline {
  border-left-color: #ef4444;
  background: rgba(239, 68, 68, 0.03);
}

.agent-status {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 70px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.online {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

.status-dot.offline {
  background: #ef4444;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
}

.status-text {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.agent-item.online .status-text {
  color: #10b981;
}

.agent-item.offline .status-text {
  color: #ef4444;
}

.agent-info {
  flex: 1;
}

.agent-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.agent-meta {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.empty-tab {
  text-align: center;
  padding: 24px 16px;
  color: var(--text-tertiary);
}

.empty-icon {
  font-size: 24px;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 13px;
  font-weight: 500;
}

.qualibot-hero-toggle:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.qualibot-hero-toggle:disabled::before {
  display: none;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  display: block;
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
}

.dashboard-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.dashboard-card {
  flex: 0 1 auto;
  width: fit-content;
  min-width: 280px;
  max-width: 400px;
}

@media (min-width: 1200px) {
  .dashboard-grid {
    gap: 24px;
  }
}

/* AI Analytics Section Styles */
.analytics-section {
  background: var(--bg-primary);
  border-radius: 20px;
  border: 1px solid var(--border-primary);
  padding: 32px;
  margin-bottom: 32px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.insights-filters {
  display: flex;
  gap: 12px;
}

.filter-select {
  padding: 8px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 140px;
}

.filter-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--accent-color), var(--accent-color-secondary));
}

.metric-card.primary::before { background: linear-gradient(90deg, #667eea, #764ba2); }
.metric-card.success::before { background: linear-gradient(90deg, #10b981, #059669); }
.metric-card.info::before { background: linear-gradient(90deg, #3b82f6, #2563eb); }
.metric-card.warning::before { background: linear-gradient(90deg, #f59e0b, #d97706); }

.metric-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px var(--shadow-primary);
  border-color: var(--border-secondary);
}

.metric-icon {
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
}

.metric-content {
  flex: 1;
}

.metric-number {
  font-size: 28px;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 4px;
}

.metric-label {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: 8px;
}

.metric-period {
  font-size: 12px;
  color: var(--text-tertiary);
  font-weight: 500;
}

.metric-trend {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 12px;
  display: inline-block;
}

.metric-trend.positive {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.metric-trend.neutral {
  background: rgba(156, 163, 175, 0.1);
  color: #6b7280;
}

/* Insights Grid */
.insights-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
}

.insight-card {
  background: var(--bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  padding: 24px;
  transition: all 0.3s ease;
}

.insight-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px var(--shadow-primary);
  border-color: var(--border-secondary);
}

.insight-card.critical {
  border-color: #ef4444;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, var(--bg-secondary) 100%);
}

.insight-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.insight-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.insight-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.insight-badge.success {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.insight-link {
  color: #667eea;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
}

.insight-link:hover {
  color: #764ba2;
}

.insight-count {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Satisfaction Chart */
.satisfaction-chart {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.satisfaction-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.satisfaction-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 80px;
}

.satisfaction-bar {
  flex: 1;
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
  max-width: 120px;
}

.satisfaction-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}

.satisfaction-fill.satisfied {
  background: linear-gradient(90deg, #10b981, #059669);
}

.satisfaction-fill.neutral {
  background: linear-gradient(90deg, #f59e0b, #d97706);
}

.satisfaction-fill.dissatisfied {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.satisfaction-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 80px;
  text-align: right;
}

/* Agents Ranking */
.agents-ranking {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.agent-rank-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  transition: all 0.3s ease;
}

.agent-rank-item:hover {
  background: var(--bg-tertiary);
  transform: translateX(4px);
}

.rank-number {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
}

.agent-info {
  flex: 1;
}

.agent-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.agent-stats {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.satisfaction-score {
  font-size: 14px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 6px;
}

.satisfaction-score.excellent {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.satisfaction-score.good {
  background: rgba(59, 130, 246, 0.1);
  color: #2563eb;
}

.satisfaction-score.average {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.satisfaction-score.poor {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Issues List */
.issues-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.issue-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: var(--bg-primary);
  border-radius: 8px;
  border-left: 3px solid #f59e0b;
}

.issue-text {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
  flex: 1;
  margin-right: 8px;
}

.issue-count {
  font-size: 12px;
  font-weight: 700;
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
}

/* Critical List */
.critical-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.critical-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-primary);
  border-radius: 8px;
  border-left: 3px solid #ef4444;
}

.critical-info {
  flex: 1;
}

.critical-phone {
  font-family: monospace;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.critical-summary {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.critical-risk {
  font-size: 14px;
  font-weight: 700;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
  padding: 4px 8px;
  border-radius: 6px;
}

.view-all-link {
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
  text-align: center;
  display: block;
}

.view-all-link:hover {
  color: #dc2626;
}

/* Business Insights Section */
.insights-section {
  margin-bottom: 32px;
}

.business-insights-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

.insights-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-primary);
}

.insights-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.insights-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 4px 0 0;
}

/* Lead Insights Card */
.insight-card.lead-insights {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
}

.insight-card.lead-insights .insight-title,
.insight-card.lead-insights .insight-count {
  color: white;
}

.lead-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-2px);
}

.stat-card.primary {
  background: rgba(255, 255, 255, 0.2);
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.stat-rate {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
}

/* Recent Activity */
.recent-activity {
  margin-top: 20px;
}

.activity-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 12px;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.activity-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10b981;
}

.activity-info {
  flex: 1;
}

.activity-text {
  font-size: 12px;
  color: white;
  font-weight: 500;
}

.activity-time {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}

/* WhatsApp Insights Card */
.insight-card.whatsapp-insights {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: none;
  color: white;
}

.insight-card.whatsapp-insights .insight-title,
.insight-card.whatsapp-insights .insight-count {
  color: white;
}

.insight-title-with-icon {
  display: flex;
  align-items: center;
  gap: 12px;
}

.insight-title-content {
  flex: 1;
}

.insight-title-content .insight-title {
  margin: 0 0 2px;
}

.insight-title-content .insight-subtitle {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  opacity: 0.8;
}

.lead-intelligence-icon {
  color: white;
  flex-shrink: 0;
}

.whatsapp-icon {
  color: white;
  flex-shrink: 0;
}

/* Motivational Message */
.motivational-message {
  margin-top: 20px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  border-left: 4px solid rgba(255, 255, 255, 0.3);
}

.motivational-text {
  font-size: 12px;
  color: white;
  font-weight: 500;
  line-height: 1.4;
}

.whatsapp-stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.qualibot-disabled {
  text-align: center;
  padding: 20px;
  color: rgba(255, 255, 255, 0.8);
}

.disabled-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.qualibot-disabled p {
  margin: 0 0 4px;
  font-weight: 500;
}

.qualibot-disabled small {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.change-summary {
  margin-top: 16px;
}

.change-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.change-icon {
  font-size: 16px;
}

.change-text {
  flex: 1;
  font-size: 12px;
  color: white;
  font-weight: 500;
}

.change-percent {
  font-size: 12px;
  font-weight: 600;
  color: white;
}

.change-indicator.positive {
  background: rgba(16, 185, 129, 0.2);
}

.change-indicator.negative {
  background: rgba(239, 68, 68, 0.2);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .business-insights-grid {
    grid-template-columns: 1fr;
  }

  .lead-stats-grid {
    grid-template-columns: 1fr;
  }

  .whatsapp-stats-grid {
    grid-template-columns: 1fr;
  }
}

/* WhatsApp Connect Insights Card Styles */
.whatsapp-insights-full-row {
  margin-bottom: 32px;
}

.whatsapp-insights-card {
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: none;
  color: white;
  padding: 20px;
}

.whatsapp-insights-card .card-header-full {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.whatsapp-insights-card .header-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.whatsapp-insights-card .header-right {
  display: flex;
  align-items: center;
}

.whatsapp-insights-card .card-title-section {
  flex: 1;
}

.whatsapp-insights-card .card-title {
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.whatsapp-insights-card .card-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-weight: 500;
}

.whatsapp-icon-large {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  opacity: 0.9;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.whatsapp-icon-large svg {
  width: 48px;
  height: 48px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.whatsapp-insights-card:hover .whatsapp-icon-large {
  opacity: 1;
  transform: scale(1.05);
}

.insights-loading,
.insights-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.insights-loading .loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.insights-error {
  color: rgba(255, 255, 255, 0.9);
}

.error-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.insights-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.today-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);
}

.main-stat {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: 900;
  color: white;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

.period-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.period-stat {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 16px 12px;
  text-align: center;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
}

.period-stat:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.period-number {
  font-size: 20px;
  font-weight: 800;
  color: white;
  line-height: 1;
  margin-bottom: 4px;
}

.period-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.last-updated {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  margin-top: 4px;
}

.update-icon {
  font-size: 12px;
  opacity: 0.8;
}

.update-text {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.qualibot-inactive-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  margin-top: 8px;
}

.inactive-icon {
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.8;
}

.inactive-text {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
}

.last-updated-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.last-updated-header .update-icon {
  font-size: 12px;
  opacity: 0.8;
}

.last-updated-header .update-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

/* Single Row Stats Layout */
.insights-content-row {
  margin-top: 8px;
}

.stats-row {
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: space-around;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  padding: 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  min-width: 120px;
}

.stat-item:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.stat-item.primary-stat {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  position: relative;
}

.stat-item .stat-number {
  font-size: 28px;
  font-weight: 900;
  color: white;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-item .stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .whatsapp-insights-card {
    min-width: auto;
    max-width: 100%;
    padding: 16px;
  }

  .whatsapp-insights-card .card-header-full {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .whatsapp-insights-card .header-right {
    align-self: flex-end;
  }

  .stats-row {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .stat-item {
    min-width: auto;
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    padding: 12px 16px;
  }

  .stat-item .stat-number {
    font-size: 24px;
  }

}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 70px; /* Space for mobile header */
  }

  .page-content {
    padding: 16px;
  }

  .welcome-section {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 20px 16px;
    margin-bottom: 20px;
  }

  .welcome-content h1 {
    font-size: 22px;
  }

  .welcome-content p {
    font-size: 14px;
  }

  .quick-stats {
    flex-direction: column;
    gap: 12px;
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  /* Enhanced mode switcher mobile styles */
  .mode-switcher-enhanced {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    padding: 16px 20px;
  }

  /* Department mode inline mobile styles */
  .department-mode-inline {
    gap: 8px;
    margin-top: 6px;
    padding-top: 6px;
  }

  .department-label-inline {
    font-size: 12px;
  }

  .settings-icon-btn {
    width: 28px;
    height: 28px;
  }

  .settings-icon-btn svg {
    width: 16px;
    height: 16px;
  }

  .switch-compact {
    width: 40px;
    height: 22px;
  }

  .slider-compact:before {
    height: 14px;
    width: 14px;
  }

  input:checked + .slider-compact:before {
    transform: translateX(18px);
  }

  .department-settings-modal {
    width: 95%;
    max-height: 90vh;
  }

  .current-mode {
    font-size: 16px;
  }

  .mode-description {
    font-size: 13px;
  }

  .toggle-btn {
    padding: 8px 16px;
    font-size: 13px;
  }

  /* WhatsApp Quick Status mobile styles */
  .whatsapp-quick-status {
    padding: 14px 16px;
    margin-bottom: 16px;
  }

  .quick-status-icon {
    width: 40px;
    height: 40px;
  }

  .quick-status-icon svg {
    width: 20px;
    height: 20px;
  }

  .quick-status-title {
    font-size: 13px;
  }

  .status-indicator {
    font-size: 12px;
    padding: 2px 8px;
  }

  .upgrade-badge {
    font-size: 10px;
    padding: 2px 6px;
  }

  /* Verified Name and Account Details Mobile Styles */
  .verified-name-inline {
    font-size: 11px;
    display: block;
    margin-top: 4px;
  }

  .account-details-inline {
    margin-top: 10px;
    padding-top: 10px;
  }

  .account-status-row {
    gap: 8px;
  }

  .status-item-inline {
    gap: 4px;
    font-size: 11px;
  }

  .status-item-inline svg {
    width: 12px;
    height: 12px;
  }

  .status-badge-small {
    font-size: 10px;
    padding: 3px 8px;
  }

  /* Mode Toggle Buttons Mobile Styles */
  .mode-toggle {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .toggle-btn {
    width: 100%;
    padding: 10px 16px;
    font-size: 14px;
    white-space: nowrap;
  }

  .mode-status {
    gap: 12px;
  }

  .mode-icon {
    font-size: 18px;
  }

  .mode-info {
    flex: 1;
  }

  /* Mobile Modal Styles */
  .confirmation-modal {
    width: 95%;
    margin: 10px;
  }

  .modal-header {
    padding: 20px 20px 12px;
  }

  .modal-icon {
    font-size: 40px;
    margin-bottom: 8px;
  }

  .modal-title {
    font-size: 18px;
  }

  .modal-body {
    padding: 0 20px 20px;
  }

  .modal-message {
    font-size: 15px;
  }

  .modal-submessage {
    font-size: 13px;
  }

  .modal-actions {
    padding: 12px 20px 20px;
    flex-direction: column;
  }

  .modal-btn {
    width: 100%;
    min-width: auto;
  }

  /* Mobile Analytics Styles */
  .analytics-section {
    padding: 20px 16px;
    margin-bottom: 20px;
  }

  .section-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
    margin-bottom: 20px;
  }

  .section-title {
    font-size: 20px;
    text-align: center;
  }

  .insights-filters {
    flex-direction: column;
    gap: 8px;
  }

  .filter-select {
    min-width: auto;
    width: 100%;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 20px;
  }

  .metric-card {
    flex-direction: column;
    text-align: center;
    padding: 16px;
    gap: 8px;
  }

  .metric-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .metric-number {
    font-size: 22px;
  }

  .insights-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .insight-card {
    padding: 16px;
  }

  .satisfaction-chart {
    gap: 12px;
  }

  .satisfaction-bar {
    max-width: 80px;
  }

  .satisfaction-value {
    min-width: 60px;
  }

  /* Lead Intelligence Center Styles - Matching WhatsApp Card Design */
  .lead-intelligence-full-row {
    margin-bottom: 32px;
  }

  .lead-intelligence-card.full-width {
    width: 100%;
    min-width: auto;
    max-width: none;
  }

  .whatsapp-insights-full-row {
    width: calc(100vw - 280px) !important;
    margin-left: -32px !important;
    margin-right: -32px !important;
    padding-left: 32px !important;
    padding-right: 32px !important;
    position: relative;
  }

  .whatsapp-insights-card.full-width,
  .whatsapp-insights-full-row .whatsapp-insights-card {
    width: 100% !important;
    min-width: auto !important;
    max-width: none !important;
    flex: none !important;
  }

  /* Mobile responsive */
  @media (max-width: 768px) {
    .whatsapp-insights-full-row {
      margin-left: -16px !important;
      margin-right: -16px !important;
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }

  .dashboard-card.lead-intelligence-card,
  .metrics-grid .dashboard-card.lead-intelligence-card,
  .metrics-grid .lead-intelligence-card,
  div.dashboard-card.lead-intelligence-card,
  .lead-intelligence-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    background-color: #667eea !important;
    border: none !important;
    color: white !important;
    padding: 20px !important;
    border-radius: 16px !important;
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3) !important;
  }

  /* Override any CSS variable backgrounds - multiple selectors */
  .dashboard-card.lead-intelligence-card,
  .metrics-grid .dashboard-card.lead-intelligence-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    background-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  }

  .lead-intelligence-card .card-header-full {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .lead-intelligence-card .header-left {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .lead-intelligence-card .header-right {
    display: flex;
    align-items: center;
  }

  .lead-intelligence-card .card-title-section {
    flex: 1;
  }

  .lead-intelligence-card .card-title {
    font-size: 18px;
    font-weight: 700;
    color: white;
    margin: 0 0 4px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .lead-intelligence-card .card-subtitle {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
    font-weight: 500;
  }

  .lead-icon-large {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    opacity: 0.9;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .lead-icon-large svg {
    width: 48px;
    height: 48px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  .lead-intelligence-card:hover .lead-icon-large {
    opacity: 1;
    transform: scale(1.05);
  }

  /* Reuse existing stats styles from WhatsApp card */
  .lead-intelligence-card .insights-content-row {
    margin-top: 8px;
  }

  .lead-intelligence-card .stats-row {
    display: flex;
    gap: 20px;
    align-items: center;
    justify-content: space-around;
    margin-bottom: 20px;
  }

  .lead-intelligence-card .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    flex: 1;
    padding: 12px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    transition: all 0.3s ease;
    min-width: 100px;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .lead-intelligence-card .stat-item:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  .lead-intelligence-card .stat-item.primary-stat {
    background: rgba(255, 255, 255, 0.15);
    border: 2px solid rgba(255, 255, 255, 0.3);
    position: relative;
  }

  .lead-intelligence-card .stat-item .stat-header {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
  }

  .lead-intelligence-card .stat-item .stat-number {
    font-size: 22px;
    font-weight: 900;
    color: white;
    line-height: 1;
  }

  .lead-intelligence-card .stat-item .stat-secondary {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1;
  }

  .lead-intelligence-card .stat-item .stat-label {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
  }

  .lead-intelligence-card .stat-item .stat-completion-rate {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    margin-bottom: 8px;
    padding: 2px 4px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    display: inline-block;
  }

  .lead-intelligence-card .stat-trend {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 12px;
    backdrop-filter: blur(10px);
  }

  .lead-intelligence-card .stat-trend.positive {
    background: rgba(16, 185, 129, 0.3);
    color: rgba(255, 255, 255, 0.95);
  }

  .lead-intelligence-card .stat-trend.negative {
    background: rgba(239, 68, 68, 0.3);
    color: rgba(255, 255, 255, 0.95);
  }

  .lead-intelligence-card .stat-trend.neutral {
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);
  }

  /* Recent Activity Section */
  .recent-activity-section {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  .activity-title {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .activity-items {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .activity-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    backdrop-filter: blur(5px);
  }

  .activity-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .activity-dot.success {
    background: #10b981;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
  }

  .activity-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .activity-text {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;
    line-height: 1.2;
  }

  .activity-phone {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 400;
    font-family: monospace;
  }

  .activity-time {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    white-space: nowrap;
  }

  /* Motivation Banner */
  .motivation-banner {
    margin-top: 16px;
    padding: 12px 16px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    text-align: center;
  }

  .motivation-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .motivation-icon {
    font-size: 16px;
    opacity: 0.9;
  }

  .motivation-text {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
  }

  /* Desktop and tablet styles (default) */
  .lead-intelligence-card .stat-item {
    min-width: 100px;
    max-width: 150px;
  }

  .lead-intelligence-card .stat-item .stat-header {
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .lead-intelligence-card .stat-item .stat-secondary {
    font-size: 12px;
  }

  .lead-intelligence-card .stat-item .stat-label {
    text-align: center;
    line-height: 1.2;
    font-size: 11px;
  }

  .lead-intelligence-card .stat-item .stat-completion-rate {
    text-align: center;
    width: fit-content;
    margin: 0 auto 8px;
    font-size: 10px;
    padding: 1px 4px;
  }

  /* Mobile specific lead intelligence styles */
  @media (max-width: 768px) {
    .lead-intelligence-card .card-header-full {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }

    .lead-intelligence-card .header-right {
      align-self: flex-end;
    }

    .lead-intelligence-card .stats-row {
      flex-direction: column;
      gap: 12px;
      align-items: stretch;
    }

    .lead-intelligence-card .stat-item {
      min-width: auto;
      max-width: none;
      width: 100%;
      flex-direction: row;
      justify-content: space-between;
      padding: 12px 16px;
    }

    .lead-intelligence-card .stat-item .stat-header {
      flex-direction: row;
      align-items: baseline;
      gap: 4px;
    }

    .lead-intelligence-card .stat-item .stat-secondary {
      font-size: 16px;
    }

    .lead-intelligence-card .stat-item .stat-label {
      text-align: left;
    }

    .lead-intelligence-card .stat-item .stat-completion-rate {
      text-align: left;
      margin: 0 0 8px;
    }

    .lead-intelligence-card .stat-item .stat-number {
      font-size: 24px;
    }

    .recent-activity-section {
      margin-top: 16px;
      padding-top: 16px;
    }

    .activity-item {
      padding: 8px 10px;
    }

    .motivation-banner {
      margin-top: 12px;
      padding: 10px 12px;
    }
  }
}

/* Integration Disabled Overlay Styles */
.integration-disabled {
  position: relative;
}

.integration-disabled-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(4px);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 20px;
}

.disabled-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  animation: fadeInScale 0.3s ease;
}

.disabled-logos-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.disabled-logo {
  height: 43px;
  width: auto;
  opacity: 0.7;
  filter: grayscale(20%) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.disabled-logo-odoo {
  height: 48px;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.disabled-icon {
  color: #ef4444;
  opacity: 0.9;
  filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.3));
}

.disabled-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.disabled-message {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
  max-width: 250px;
}

.integration-disabled .qualibot-hero-content,
.integration-disabled .crm-hero-content {
  filter: blur(2px);
  opacity: 0.4;
  pointer-events: none;
}

.integration-disabled button {
  pointer-events: none;
}

/* WhatsApp Hero Card Styles - Consistent with QualiBot */
.whatsapp-hero-card {
  background: linear-gradient(135deg, #25D366 0%, #128C7E 50%, #075E54 100%);
  border: 2px solid transparent;
  background-clip: padding-box;
  position: relative;
  overflow: hidden;
  padding: 18px;
}

.whatsapp-hero-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 18px;
  padding: 2px;
  background: linear-gradient(135deg, #25D366, #128C7E, #075E54);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

.whatsapp-hero-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  z-index: 1;
  color: white;
}

.whatsapp-visual-section {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 60px;
  margin-bottom: 8px;
}

.whatsapp-hero-icon {
  width: 50px;
  height: 50px;
  transition: transform 0.3s ease;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px;
}

.whatsapp-hero-card:hover .whatsapp-hero-icon {
  transform: scale(1.1);
}

.whatsapp-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  opacity: 0;
  transition: all 0.4s ease;
}

.whatsapp-glow.active {
  opacity: 1;
  animation: whatsapp-pulse 2s infinite;
}

@keyframes whatsapp-pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.4;
  }
}

.whatsapp-info-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
}

.whatsapp-hero-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin: 0;
}

.platform-label {
  font-size: 16px;
  font-weight: 900;
  letter-spacing: 1px;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.connect-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.8px;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
}

.whatsapp-status-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
}

.whatsapp-status-row .status-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.whatsapp-status-row .status-value {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.whatsapp-status-row .status-value.connected {
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
}

.connection-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.session-info {
  display: flex;
  justify-content: center;
}

.session-id {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  font-family: monospace;
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.setup-method-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.setup-method-badge.embedded {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%);
  border-color: rgba(34, 197, 94, 0.4);
  color: #86efac;
}

.setup-method-badge svg {
  flex-shrink: 0;
}

.whatsapp-hero-recheck {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  overflow: hidden;
}

.whatsapp-hero-recheck::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.whatsapp-hero-recheck:hover::before {
  left: 100%;
}

.whatsapp-hero-recheck:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.3);
}

.whatsapp-hero-recheck:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.whatsapp-hero-recheck:disabled::before {
  display: none;
}

.recheck-icon {
  width: 14px;
  height: 14px;
  transition: transform 0.3s ease;
}

.whatsapp-hero-recheck:not(:disabled):hover .recheck-icon {
  transform: rotate(180deg);
}

.recheck-text {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* WhatsApp Setup State Styles */
.whatsapp-hero-card.setup-needed {
  background: linear-gradient(135deg, rgba(37, 211, 102, 0.1) 0%, rgba(18, 140, 126, 0.1) 100%);
  border-color: rgba(37, 211, 102, 0.3);
}

.whatsapp-setup-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 30px 20px;
  text-align: center;
}

.setup-visual {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.setup-badge {
  padding: 6px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 2px solid rgba(239, 68, 68, 0.3);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  color: #ef4444;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.setup-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.setup-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;
}

.setup-description {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
  max-width: 400px;
}

.setup-tutorial-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.setup-tutorial-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.setup-tutorial-btn:active {
  transform: translateY(0);
}

.whatsapp-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.whatsapp-hero-initialize {
  background: rgba(16, 185, 129, 0.9);
  border: 2px solid rgba(16, 185, 129, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  overflow: hidden;
}

.whatsapp-hero-initialize::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.whatsapp-hero-initialize:hover::before {
  left: 100%;
}

.whatsapp-hero-initialize:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  border-color: rgba(16, 185, 129, 1);
  background: rgba(16, 185, 129, 1);
}

.whatsapp-hero-initialize:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.whatsapp-hero-initialize:disabled::before {
  display: none;
}

.initialize-text {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* CRM Hero Card Styles */
.crm-hero-card {
  background: linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%);
  background-clip: padding-box;
  position: relative;
  overflow: hidden;
  padding: 18px;
}

.crm-hero-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 18px;
  padding: 2px;
  background: linear-gradient(135deg, #e97317, #f59e0b, #fbbf24);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  z-index: -1;
}

.crm-hero-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;
  height: 100%;
}

.crm-visual-section {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-bottom: 8px;
}

.crm-hero-icon {
  width: 120px;
  height: auto;
  transition: all 0.3s ease;
}

.crm-hero-card:hover .crm-hero-icon {
  transform: scale(1.1);
}

.crm-info-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  color: white;
}

.crm-hero-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin: 0;
  line-height: 1;
}

.crm-status-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
}

.crm-status-row .status-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-label {
  font-size: 12px;
  font-weight: 500;
  opacity: 0.9;
}

.status-value {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
}

.status-value.connected {
  background: rgba(16, 185, 129, 0.8);
  color: white;
}

.status-value.warning {
  background: rgba(245, 158, 11, 0.8);
  color: white;
}

.sync-info {
  margin-top: 4px;
}

.sync-time {
  font-size: 10px;
  opacity: 0.8;
  color: white;
}

.crm-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

.crm-hero-connect,
.crm-hero-manage,
.crm-hero-restricted {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  position: relative;
  overflow: hidden;
}

.crm-hero-connect::before,
.crm-hero-manage::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.crm-hero-connect:hover::before,
.crm-hero-manage:hover::before {
  left: 100%;
}

.crm-hero-connect:hover:not(:disabled),
.crm-hero-manage:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.3);
}

.connect-icon,
.manage-icon {
  font-size: 14px;
  width: 14px;
  height: 14px;
}

.connect-text,
.manage-text,
.restricted-text {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.crm-hero-restricted {
  background: rgba(128, 128, 128, 0.3);
  border-color: rgba(128, 128, 128, 0.4);
  cursor: not-allowed;
  opacity: 0.7;
}

.crm-hero-restricted:hover {
  transform: none;
  box-shadow: none;
}

.restricted-icon,
.restricted-text {
  opacity: 0.9;
}

/* Odoo Hero Card Distinctive Styling */
.odoo-hero-card::before {
  background: linear-gradient(135deg, #714B67 0%, #8B4F80 50%, #A65398 100%);
}

.odoo-hero-card .platform-label {
  color: #A65398;
}

/* Notification Toast */
.notification-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 10000;
  max-width: 400px;
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.notification-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 20px;
  background: var(--bg-primary);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  border-left: 4px solid;
}

.notification-container.success .notification-content {
  border-left-color: #10b981;
}

.notification-container.error .notification-content {
  border-left-color: #ef4444;
}

.notification-container.info .notification-content {
  border-left-color: #3b82f6;
}

.notification-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
}

.notification-container.success .notification-icon {
  background: #10b98120;
  color: #10b981;
}

.notification-container.error .notification-icon {
  background: #ef444420;
  color: #ef4444;
}

.notification-container.info .notification-icon {
  background: #3b82f620;
  color: #3b82f6;
}

.notification-text {
  flex: 1;
}

.notification-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.notification-message {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.notification-close {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.notification-close:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

@media (max-width: 768px) {
  .notification-container {
    top: 12px;
    right: 12px;
    left: 12px;
    max-width: none;
  }
}
</style>
